/**
 * CowLot Hat Builder
 * Custom hat configurator for Shopify
 * WCAG 2.2 AA Compliant
 *
 * NOTE: This file is NOT in active use on the live store.
 * cowlot-hat-builder.js is the active file — make all option set changes there.
 */

// Check if crown image configuration is loaded (from crown-images-config.js)
if (window.CROWN_IMAGE_CONFIG) {
  console.log('[HatBuilder] Crown image config loaded from crown-images-config.js');
} else {
  console.warn('[HatBuilder] Crown image config not found - using fallback URLs');
}

(function() {
  'use strict';

  /**
   * Initialize a Hat Builder instance
   */
  function initHatBuilder(container) {
    const sectionId = container.dataset.sectionId;
    const configEl = document.getElementById('hat-builder-config-' + sectionId);

    if (!configEl) {
      showError(container, 'Configuration not found');
      return;
    }

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      showError(container, 'Invalid configuration');
      return;
    }

    // Create the builder instance and store it on the container
    const builder = new HatBuilder(container, config);
    container._hatBuilder = builder;
  }

  /**
   * Show error state
   */
  function showError(container, message) {
    const loading = container.querySelector('.hat-builder__loading');
    const error = container.querySelector('.hat-builder__error');
    if (loading) loading.hidden = true;
    if (error) {
      error.hidden = false;
      error.querySelector('p').textContent = message;
    }
  }

  /**
   * Hat Builder Class
   */
  class HatBuilder {
    constructor(container, config) {
      this.container = container;
      this.config = config;
      this.currentStep = 0;
      this.selections = {};
      this.upgrades = [];
      this.steps = [];
      this.optionSet = null;
      this._uploadedFiles = {};
      this._prefillMode = false;
      this._gallerySource = null;
      this._editingLineKey = null;

      // Get DOM elements
      this.els = {
        loading: container.querySelector('.hat-builder__loading'),
        error: container.querySelector('.hat-builder__error'),
        main: container.querySelector('.hat-builder__container'),
        stepper: container.querySelector('.hat-builder-stepper__list'),
        progressBar: container.querySelector('.hat-builder-stepper__progress-bar'),
        content: container.querySelector('.hat-builder__content'),
        backBtn: container.querySelector('[data-action="back"]'),
        nextBtn: container.querySelector('[data-action="next"]'),
        addToCartBtn: container.querySelector('[data-action="add-to-cart"]'),
        totalDisplay: container.querySelector('.hat-builder__total'),
        announcer: container.querySelector('.hat-builder__announcer'),
        summary: container.querySelector('.hb-summary')
      };

      this.init();
    }

    // =========================================================================
    // Initialization
    // =========================================================================

    init() {
      try {
        // Parse option set
        console.log('[HatBuilder] Initializing with config:', this.config.optionSetId);
        if (!this.parseOptionSet()) {
          showError(this.container, 'Option set not found: ' + this.config.optionSetId);
          return;
        }
        console.log('[HatBuilder] Option set loaded:', this.steps.length, 'steps');

        // CRITICAL: Sync variant ID from container if it was already set by dropdown before builder loaded
        // This fixes race condition where dropdown fires before MutationObserver is set up
        const currentVariantId = parseInt(this.container.dataset.variantId, 10);
        if (currentVariantId && currentVariantId !== this.config.variantId) {
          console.log('[HatBuilder] Syncing variant ID from dropdown:', currentVariantId, '(was:', this.config.variantId, ')');
          this.config.variantId = currentVariantId;
        }

        // Pre-fill selections from URL parameters (e.g., ?crown=cattleman)
        this._readUrlParams();

        // Check if we're editing an existing cart item
        this._checkEditMode();

        // Edit-cart only: seed the customer's previously-chosen size so they
        // don't have to re-pick. New orders intentionally start with no
        // hatSize selection so the required step forces an explicit click.
        if (this._editingLineKey) {
          this._seedHatSizeFromCurrentVariant();
        }

        // Build the stepper
        this.buildStepper();



        // Render first step or welcome screen
        if (this._prefillMode) {
          this._renderWelcomeScreen();
        } else {
          this.renderStep(0);
        }

        // Bind events
        this.bindEvents();

        // Listen for same-page crown pre-selection from gallery "Build This Hat" buttons
        this._bindCrownPreselectEvent();

        // Preload images for better performance (non-blocking)
        this._preloadImages();

        // Load customization fee product variants (non-blocking)
        this.feeVariants = [];
        this._loadFeeProduct();

        // Load free hat can variants if this option set includes a hat can step (non-blocking)
        this.freeCanVariants = [];
        this._loadFreeCanProduct();

        // Show the builder
        this.els.loading.hidden = true;
        this.els.main.hidden = false;

        // Announce to screen readers
        this.announce('Hat customization options loaded. Step 1 of ' + this.steps.length);
        console.log('[HatBuilder] Ready');
      } catch (err) {
        console.error('[HatBuilder] Init failed:', err);
        showError(this.container, 'Initialization error: ' + err.message);
      }
    }

    _preloadImages() {
      if (!window.CROWN_IMAGE_CONFIG) return;
      
      const imagesToPreload = [];
      
      // Collect all image URLs from config
      ['crowns', 'brimFronts', 'brimShapes'].forEach(category => {
        const items = window.CROWN_IMAGE_CONFIG[category];
        if (items) {
          Object.values(items).forEach(path => {
            if (typeof path === 'string') {
              const url = path.startsWith('http') ? path : this.getImageUrl(path);
              if (url) imagesToPreload.push(url);
            }
          });
        }
      });
      
      // Preload images in the background (non-blocking)
      if (imagesToPreload.length > 0) {
        console.log('[HatBuilder] Preloading', imagesToPreload.length, 'images for better performance');
        
        // Use requestIdleCallback if available, otherwise setTimeout
        const preloadFn = () => {
          imagesToPreload.forEach(url => {
            const img = new Image();
            img.src = url;
          });
        };
        
        if ('requestIdleCallback' in window) {
          requestIdleCallback(preloadFn);
        } else {
          setTimeout(preloadFn, 100);
        }
      }
    }

    async _loadFeeProduct() {
      const handle = this.config.feeProductHandle;
      if (!handle) {
        console.log('[HatBuilder] No fee product handle configured');
        return;
      }

      try {
        const response = await fetch('/products/' + handle + '.js');
        if (!response.ok) {
          console.warn('[HatBuilder] Fee product not found:', handle);
          return;
        }
        const product = await response.json();
        // Store variants sorted by price ascending for easy matching
        this.feeVariants = product.variants
          .map(v => ({ id: v.id, price: v.price, title: v.title }))
          .sort((a, b) => a.price - b.price);
        console.log('[HatBuilder] Fee product loaded:', this.feeVariants.length, 'variants');
      } catch (err) {
        console.warn('[HatBuilder] Could not load fee product:', err.message);
      }
    }

    async _loadFreeCanProduct() {
      if (!this.steps.find(s => s.id === 'hatCanColor')) return;
      try {
        const response = await fetch('/products/large-western-hat-can.js');
        if (!response.ok) {
          console.warn('[HatBuilder] Hat can product not found');
          return;
        }
        const product = await response.json();
        this.freeCanVariants = product.variants.map(v => ({ id: v.id, title: v.title }));
        console.log('[HatBuilder] Hat can variants loaded:', this.freeCanVariants.length);
      } catch (err) {
        console.warn('[HatBuilder] Could not load hat can product:', err.message);
      }
    }

    _findFeeVariant(upgradeAmountCents) {
      if (!this.feeVariants.length || upgradeAmountCents <= 0) return null;

      // Sort variants by price ascending for reliable matching
      const sorted = [...this.feeVariants].sort((a, b) => a.price - b.price);

      // Find exact match first
      let match = sorted.find(v => v.price === upgradeAmountCents);
      if (match) return match;

      // Find the smallest variant that covers the upgrade amount (never undercharge)
      match = sorted.find(v => v.price >= upgradeAmountCents);
      if (match) return match;

      // If upgrade exceeds all variants, use the highest one
      return sorted[sorted.length - 1];
    }

    parseOptionSet() {
      const optionSets = this.config.optionSets;
      const optionSetId = this.config.optionSetId;

      // Check if metafield data is available
      const libs = this.config.libraries;
      const allLibsNull = !libs || Object.values(libs).every(v => v === null || v === undefined);

      if (allLibsNull || !optionSets || !optionSets[optionSetId]) {
        // Use embedded fallback config
        const embedded = this._getEmbeddedConfig();
        this.config.libraries = embedded.libraries;
        this.config.optionSets = embedded.optionSets;
      }

      let setData = this.config.optionSets[optionSetId];
      if (!setData) {
        // Unknown set ID — fall back to default rather than showing an error
        console.warn('[HatBuilder] Option set "' + optionSetId + '" not found, falling back to rodeo-king-felt-4.5');
        setData = this.config.optionSets['rodeo-king-felt-4.5'];
      }
      if (!setData) return false;

      this.optionSet = setData;
      this.steps = setData.steps || [];

      // Re-enabled 2026-04-28. Customers were skipping the visible top-of-page
      // dropdown (especially in prefill/gallery mode) and shipping at whatever
      // ?variant= URL pinned (often the smallest). The required builder step
      // forces an explicit size click. The visible dropdown stays as a
      // secondary path; both are kept in sync.
      this._injectHatSizeStep();

      // Modify confirmation step for 30X+ quality hats
      this._adjustConfirmationFor30XPlus();
      
      return this.steps.length > 0;
    }

    _isPremiumQuality() {
      // Restrict the no-fit-guide acknowledgement to the specified felt qualities.
      const title = this.config.productTitle || '';
      const tags = this.config.productTags || [];
      const quality = this.config.productQuality || '';
      const isFelt = /felt/i.test(title) || tags.some(tag => /felt/i.test(tag));
      if (!isFelt) return false;

      const qualitySources = [title, ...tags, String(quality)];
      return qualitySources.some(source => {
        const match = String(source).match(/(\d+)x/i);
        return match && [30, 50, 60, 100, 500].includes(parseInt(match[1], 10));
      });
    }

    _injectHatSizeStep() {
      const variants = this.config.hatSizeVariants;
      if (!Array.isArray(variants) || variants.length < 2) return;

      // Build option list from product variants. `option1` is the size value
      // (Shopify's first option on a hat product). Sold-out variants render as
      // disabled buttons.
      const options = variants
        .filter(v => v && v.option1)
        .map(v => ({
          value: String(v.option1),
          label: String(v.option1),
          disabled: !v.available,
          description: !v.available ? 'Sold Out' : ''
        }));
      if (!options.length) return;

      const sizeLabel = this.config.hatSizeOptionName || 'Hat Size';

      const hatSizeStep = {
        id: 'hatSize',
        order: 0,
        title: 'Select ' + sizeLabel,
        subtitle: 'Size is the foundation of your hat. Please choose carefully — not sure which size? See our Fit Guide.',
        required: true,
        type: 'buttonGroup',
        options: options
        // No lineItemProperty: size is already encoded in Shopify's variant title
      };

      // Shift every existing step's order by +1 so stepper numbering stays correct
      this.steps.forEach(s => { if (typeof s.order === 'number') s.order += 1; });
      this.steps.unshift(hatSizeStep);
      // NOTE: We deliberately do NOT seed this.selections.hatSize here for new
      // orders. Seeding caused customers in prefill/gallery mode to skip past
      // the size step (because _findFirstIncompleteStep saw a non-empty
      // value), which is what shipped wrong-size orders. The size step is
      // required and the customer must click. Edit-cart flows seed in init()
      // separately, where _editingLineKey is known.

      console.log('[HatBuilder] Hat size step injected. Variants:', variants.length);
    }

    _seedHatSizeFromCurrentVariant() {
      // Used only on edit-cart return: pre-fill selections.hatSize so the
      // customer doesn't have to re-pick a size they already chose. Safe
      // because in edit mode the URL variant came from the original cart
      // line, not from a default.
      const variants = Array.isArray(this.config.hatSizeVariants) ? this.config.hatSizeVariants : [];
      if (variants.length < 2) return;
      const currentVariantId = parseInt(this.container.dataset.variantId, 10) || this.config.variantId;
      const currentVariant = variants.find(v => Number(v.id) === Number(currentVariantId));
      if (currentVariant && currentVariant.available && currentVariant.option1) {
        this.selections.hatSize = String(currentVariant.option1);
        console.log('[HatBuilder] Edit-cart: seeded hatSize =', this.selections.hatSize);
      }
    }

    _syncHatSizeToVariantSelect(sizeValue) {
      const sizeSelect = document.querySelector('.hat-product__variant-select');
      if (!sizeSelect) return;
      if (sizeSelect.value !== sizeValue) {
        sizeSelect.value = sizeValue;
        sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    _adjustConfirmationFor30XPlus() {
      if (!this._isPremiumQuality()) return;

      // Find confirmation step and update the "confident" option description
      const confirmStep = this.steps.find(s => s.id === 'confirmation');
      if (!confirmStep || !confirmStep.fields) return;

      const sizeConfirmField = confirmStep.fields.find(f => f.id === 'sizeConfirmation');
      if (!sizeConfirmField || !sizeConfirmField.options) return;

      const confidentOption = sizeConfirmField.options.find(opt => opt.value === 'confident');
      if (confidentOption) {
        confidentOption.description = 'Without a Perfect Fit Guide, this hat cannot be returned once shaped. We strongly recommend selecting "I will provide a Perfect Fit Guide" below.';
      }

      // Add required acknowledgment checkbox for "confident" selection
      const ackFieldExists = confirmStep.fields.find(f => f.id === 'premium30xAcknowledgment');
      if (!ackFieldExists) {
        const fitGuideFieldIndex = confirmStep.fields.findIndex(f => f.id === 'requestFitGuide');
        const insertIndex = fitGuideFieldIndex >= 0 ? fitGuideFieldIndex : confirmStep.fields.length;
        
        confirmStep.fields.splice(insertIndex, 0, {
          id: 'premium30xAcknowledgment',
          label: 'Premium Hat Policy',
          type: 'checkbox',
          required: true,
          showWhen: { sizeConfirmation: ['confident'] },
          lineItemProperty: 'Felt Hat No Fit Guide Acknowledgment',
          checkboxLabel: 'I understand that without completing the Cow Lot Fit Guide, my hat is not eligible for return or refund',
          helpText: ''
        });
      }
    }

    // =========================================================================
    // Embedded Config (POC fallback when metafields not set)
    // =========================================================================

    _getEmbeddedConfig() {
      return {
        libraries: {
          // Standard head shapes — most factories cannot do Round, so it is
          // intentionally excluded. Atwood-specific option sets use
          // `atwoodHeadShapes` below.
          headShapes: [
            { value: 'oval', label: 'Oval', default: true, svg: '<svg viewBox="0 0 60 72" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="36" rx="22" ry="28" stroke="currentColor" stroke-width="2.5"/></svg>' },
            { value: 'long-oval', label: 'Long Oval', svg: '<svg viewBox="0 0 60 80" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="40" rx="19" ry="33" stroke="currentColor" stroke-width="2.5"/></svg>' },
            { value: 'extra-long-oval', label: 'Extra Long Oval', svg: '<svg viewBox="0 0 60 88" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="44" rx="16" ry="37" stroke="currentColor" stroke-width="2.5"/></svg>' },
            { value: 'other', label: 'Other / Not Sure', svg: '<svg viewBox="0 0 60 72" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="36" rx="22" ry="28" stroke="currentColor" stroke-width="2.5" stroke-dasharray="5 4"/><text x="30" y="40" text-anchor="middle" fill="currentColor" font-size="18" font-weight="bold">?</text></svg>' }
          ],
          // Atwood-only head shapes — includes Round.
          atwoodHeadShapes: [
            { value: 'oval', label: 'Oval', default: true, svg: '<svg viewBox="0 0 60 72" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="36" rx="22" ry="28" stroke="currentColor" stroke-width="2.5"/></svg>' },
            { value: 'long-oval', label: 'Long Oval', svg: '<svg viewBox="0 0 60 80" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="40" rx="19" ry="33" stroke="currentColor" stroke-width="2.5"/></svg>' },
            { value: 'extra-long-oval', label: 'Extra Long Oval', svg: '<svg viewBox="0 0 60 88" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="44" rx="16" ry="37" stroke="currentColor" stroke-width="2.5"/></svg>' },
            { value: 'round', label: 'Round', svg: '<svg viewBox="0 0 68 72" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="34" cy="36" rx="26" ry="27" stroke="currentColor" stroke-width="2.5"/></svg>' },
            { value: 'other', label: 'Other / Not Sure', svg: '<svg viewBox="0 0 60 72" fill="none" xmlns="http://www.w3.org/2000/svg"><ellipse cx="30" cy="36" rx="22" ry="28" stroke="currentColor" stroke-width="2.5" stroke-dasharray="5 4"/><text x="30" y="40" text-anchor="middle" fill="currentColor" font-size="18" font-weight="bold">?</text></svg>' }
          ],
          crowns: {
            traditional: [
              { value: 'association', label: 'Association', desc: 'Tall \u2022 5\u201D front, 4\u00BC\u201D back \u2022 1\u00BD\u201D wide \u2022 Slanted dents', image: 'crowns/association.webp', imageFull: 'crowns/association.webp', alt: 'Association crown style' },
              { value: 'brick', label: 'Brick', desc: '4\u00BD\u201D crown \u2022 3\u201D\u00D76\u201D popped up inside', image: 'crowns/brick.webp', imageFull: 'crowns/brick.webp', alt: 'Brick crown - flat top rectangular style' },
              { value: 'cattleman', label: 'Cattleman', desc: '4\u00BD\u201D front, 4\u00BE\u201D back \u2022 1\u00BD\u201D wide \u2022 Elongated oval dents', image: 'crowns/cattleman.webp', imageFull: 'crowns/cattleman.webp', alt: 'Cattleman - classic western style with center crease' },
              { value: 'cattleman-low', label: 'Low Cattleman', desc: 'Low profile \u2022 4\u00BC\u201D crown \u2022 1\u00BD\u201D wide \u2022 Elongated oval dents', image: 'crowns/low-cattleman.webp', imageFull: 'crowns/low-cattleman.webp', alt: 'Low Cattleman - lower profile version' },
              { value: 'cattleman-punched', label: 'Punched Out Cattleman', desc: 'Tall \u2022 4\u00BD\u201D front, 6\u201D middle \u2022 Popped out side dents & center', image: 'crowns/punched-cattleman.svg', imageFull: 'crowns/punched-cattleman.svg', alt: 'Punched out Cattleman style' },
              { value: 'cattleman-wide', label: 'Wide Cattleman', desc: '4\u00BD\u201D crown \u2022 2\u201D wide \u2022 Elongated oval dents', image: 'crowns/wide-cattleman.webp', imageFull: 'crowns/wide-cattleman.webp', alt: 'Wide Cattleman style' },
              { value: 'clint', label: 'Clint', desc: 'Low profile \u2022 4\u00BC\u201D crown \u2022 4\u201D wide tear drop \u2022 Slanted dents', image: 'crowns/clint.webp', imageFull: 'crowns/clint.webp', alt: 'Clint crown style' },
              { value: 'gambler', label: 'Gambler', desc: '4\u00BC\u201D crown \u2022 4\u201D wide, rounds off at back \u2022 No side dents', image: 'crowns/gambler.webp', imageFull: 'crowns/gambler.webp', alt: 'Gambler - oval dent style' },
              { value: 'gus', label: 'Gus', desc: '4\u201D front, 5\u00BE\u201D back \u2022 1\u00BD\u201D\u00D76\u00BD\u201D wide \u2022 4\u201D slant dents', image: 'crowns/gus.webp', imageFull: 'crowns/gus.webp', alt: 'Gus - tall pinched front style' },
              { value: 'luke', label: 'Luke (CHL)', desc: 'Tall \u2022 5\u00BD\u201D crown \u2022 Round width \u2022 Small round dents', image: 'crowns/luke.webp', imageFull: 'crowns/luke.webp', alt: 'Luke - modern pinched style' },
              { value: 'minick', label: 'Minick', desc: 'Tall \u2022 5\u201D crown \u2022 2\u201D wide \u2022 2\u00BE\u201D round oval dents', image: 'crowns/minick.svg', imageFull: 'crowns/minick.svg', alt: 'Minick crown style' },
              { value: 'open', label: 'Open (unshaped)', desc: 'Unshaped open crown \u2022 Sold as-is', image: 'crowns/open.webp', imageFull: 'crowns/open.webp', alt: 'Open crown - unshaped' },
              { value: 'puncher', label: 'Puncher', desc: 'Tall \u2022 6\u201D open front \u2022 Side dents only \u2022 2\u00BD\u201D wide 4\u201D\u00D72\u201D round', image: 'crowns/puncher.webp', imageFull: 'crowns/puncher.webp', alt: 'Puncher crown style' },
              { value: 'ranch', label: 'Ranch', desc: 'Like Cattleman, taller back \u2022 4\u215C\u201D front, 4\u215E\u201D back \u2022 Elongated oval dents', image: 'crowns/ranch.webp', imageFull: 'crowns/ranch.webp', alt: 'Ranch crown style' },
              { value: 'round-top', label: 'Round Top (Telescope)', desc: '4\u00BC\u201D crown \u2022 Round width \u2022 No side dents', image: 'crowns/round-top.webp', imageFull: 'crowns/round-top.webp', alt: 'Round top telescope crown' },
              { value: 'square-top', label: 'Square Top', desc: '4\u00BD\u201D crown \u2022 4\u201D\u00D75\u201D popped up inside \u2022 No side dents', image: 'crowns/square-top.webp', imageFull: 'crowns/square-top.webp', alt: 'Square top crown' }
            ],
            specialty: [
              { value: 'brick-cross', label: 'Brick with Cross', desc: 'Brick shape \u2022 Cross in center popped out \u2022 Front or back placement', image: 'crowns/brick-cross.webp', imageFull: 'crowns/brick-cross.webp', alt: 'Brick crown with cross design' },
              { value: 'brick-initial', label: 'Brick with Initial', desc: 'Brick shape \u2022 Initial in center popped out', image: 'crowns/brick-initial.webp', imageFull: 'crowns/brick-initial.webp', alt: 'Brick crown with custom initial', requiresField: 'brickInitial' },
              { value: 'diamond-top', label: 'Diamond Top', desc: 'Diamond shaped top \u2022 4\u00BD\u201D crown', image: 'crowns/diamond-top.webp', imageFull: 'crowns/diamond-top.webp', alt: 'Diamond top crown' },
              { value: 'horseshoe', label: 'Horseshoe', desc: 'Low profile \u2022 4\u00BC\u201D crown \u2022 Mule kick front \u2022 Round back', image: 'crowns/horseshoe.webp', imageFull: 'crowns/horseshoe.webp', alt: 'Horseshoe crown design' },
              { value: 'minick-heart', label: 'Minick with Heart', desc: 'Heart on side dents \u2022 Standard or popped out', image: 'crowns/minick-heart.webp', imageFull: 'crowns/minick-heart.webp', alt: 'Minick crown with heart' },
              { value: 'rose', label: 'Rose', desc: 'Rose design crown', image: 'crowns/rose.webp', imageFull: 'crowns/rose.webp', alt: 'Rose crown design' }
            ],
            allFlat: [
              { value: 'association', label: 'Association', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_association.webp?v=1776136051', alt: 'Association crown style' },
              { value: 'brick', label: 'Brick', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_brick.webp?v=1776135162', alt: 'Brick crown' },
              { value: 'brick-cross', label: 'Brick with Cross', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_brick-cross.webp?v=1776136125', alt: 'Brick crown with cross design' },
              { value: 'cattleman', label: 'Cattleman', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_cattleman.webp?v=1776134442', alt: 'Cattleman style' },
              { value: 'cattleman-low', label: 'Low Cattleman', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_low-cattleman.webp?v=1776134479', alt: 'Low Cattleman style' },
              { value: 'cattleman-punched', label: 'Punched Out Cattleman', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_punched-cattleman.webp?v=1776134560', alt: 'Punched out Cattleman style' },
              { value: 'cattleman-wide', label: 'Wide Cattleman', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_wide-cattleman.webp?v=1776134722', alt: 'Wide Cattleman style' },
              { value: 'clint', label: 'Clint', image: 'crowns/clint.webp', alt: 'Clint crown style' },
              { value: 'diamond-top', label: 'Diamond Top', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_diamond-top.webp?v=1776136606', alt: 'Diamond top crown' },
              { value: 'gambler', label: 'Gambler', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_gambler.webp?v=1776134780', alt: 'Gambler style' },
              { value: 'gus', label: 'Gus', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_gus.webp?v=1776134833', alt: 'Gus style' },
              { value: 'horseshoe', label: 'Horseshoe', image: 'crowns/horseshoe.webp', alt: 'Horseshoe crown design' },
              { value: 'luke', label: 'Luke (CHL)', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_luke.jpg?v=1776136543', alt: 'Luke style' },
              { value: 'minick', label: 'Minick', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_minick.webp?v=1776135961', alt: 'Minick crown style' },
              { value: 'open', label: 'Open (unshaped)', image: 'crowns/open.webp', alt: 'Open crown - unshaped' },
              { value: 'puncher', label: 'Puncher', image: 'crowns/puncher.webp', alt: 'Puncher crown style' },
              { value: 'ranch', label: 'Ranch', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_ranch.webp?v=1776134925', alt: 'Ranch crown style' },
              { value: 'rose', label: 'Rose', image: 'crowns/rose.webp', alt: 'Rose crown design' },
              { value: 'round-top', label: 'Round Top (Telescope)', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/crowns_round-top.webp?v=1776135747', alt: 'Round top telescope crown' },
              { value: 'square-top', label: 'Square Top', image: 'https://cdn.shopify.com/s/files/1/0478/1673/6918/files/Crowns_Squaretop.webp?v=1776134875', alt: 'Square top crown' }
            ]
          },
          // Straw brim fronts — "flat" replaced by "other" (2026-04-28)
          strawBrimFronts: [
            { value: 'pointed', label: 'Pointed', image: 'brims/front-pointed.webp', alt: 'Pointed brim front' },
            { value: 'round', label: 'Round', image: 'brims/front-round.svg', alt: 'Rounded brim front' },
            { value: 'square-6', label: 'Square 6"', image: 'brims/front-square.svg', alt: 'Square 6 inch brim front' },
            { value: 'wide-8', label: 'Wide 8"', image: 'brims/front-wide.webp', alt: 'Wide 8 inch brim front' },
            { value: 'extra-wide-10', label: 'Extra Wide 10"', image: 'brims/front-extra-wide.webp', alt: 'Extra wide 10 inch brim front' },
            { value: 'other', label: 'Other', alt: 'Custom brim front - describe your preference' }
          ],
          // Straw brim shapes — "retro" and "come-and-go" removed (2026-04-28).
          // "Other" added so customers can describe an unusual shape.
          strawBrimShapes: [
            { value: 'flat', label: 'Flat', image: 'brims/shape-flat.webp', alt: 'Flat brim shape - unshaped' },
            { value: 'low', label: 'Low', image: 'brims/shape-low.webp', alt: 'Low brim shape' },
            { value: 'george', label: 'George', image: 'brims/shape-george.svg', alt: 'George Strait style brim' },
            { value: 'relaxed-taco', label: 'Relaxed Taco', image: 'brims/shape-relaxed-taco.webp', alt: 'Relaxed taco brim shape' },
            { value: 'half-taco', label: 'Half Taco', image: 'brims/shape-half-taco.webp', alt: 'Half taco brim shape' },
            { value: 'taco', label: 'Taco', image: 'brims/shape-taco.webp', alt: 'Full taco brim shape' },
            { value: 'other', label: 'Other', alt: 'Custom brim shape - describe your preference' }
          ],
          // Felt brim fronts — "flat" replaced by "other" (2026-04-28)
          brimFronts: [
            { value: 'pointed', label: 'Pointed', image: 'brims/front-pointed.webp', imageFull: 'brims/front-pointed.webp', alt: 'Pointed brim front' },
            { value: 'round', label: 'Round', image: 'brims/front-round.svg', imageFull: 'brims/front-round.svg', alt: 'Rounded brim front' },
            { value: 'square-6', label: 'Square 6"', image: 'brims/front-square.svg', imageFull: 'brims/front-square.svg', alt: 'Square 6 inch brim front' },
            { value: 'wide-8', label: 'Wide 8"', image: 'brims/front-wide.webp', imageFull: 'brims/front-wide.webp', alt: 'Wide 8 inch brim front' },
            { value: 'extra-wide-10', label: 'Extra Wide 10"', image: 'brims/front-extra-wide.webp', imageFull: 'brims/front-extra-wide.webp', alt: 'Extra wide 10 inch brim front' },
            { value: 'buckaroo', label: 'Buckaroo', image: 'brims/front-buckaroo.svg', imageFull: 'brims/front-buckaroo.svg', alt: 'Buckaroo style brim front' },
            { value: 'other', label: 'Other', alt: 'Custom brim front - describe your preference' }
          ],
          // Felt brim shapes — "retro" and "come-and-go" removed (2026-04-28).
          // Flat image is sourced from crown-images-config.js (buckaroo_brim.webp).
          // "Other" added so customers can describe an unusual shape.
          brimShapes: [
            { value: 'flat', label: 'Flat', image: 'brims/shape-flat.webp', imageFull: 'brims/shape-flat.webp', alt: 'Flat brim shape - unshaped' },
            { value: 'low', label: 'Low', image: 'brims/shape-low.webp', imageFull: 'brims/shape-low.webp', alt: 'Low brim shape' },
            { value: 'george', label: 'George', image: 'brims/shape-george.svg', imageFull: 'brims/shape-george.svg', alt: 'George Strait style brim' },
            { value: 'relaxed-taco', label: 'Relaxed Taco', image: 'brims/shape-relaxed-taco.webp', imageFull: 'brims/shape-relaxed-taco.webp', alt: 'Relaxed taco brim shape' },
            { value: 'half-taco', label: 'Half Taco', image: 'brims/shape-half-taco.webp', imageFull: 'brims/shape-half-taco.webp', alt: 'Half taco brim shape' },
            { value: 'taco', label: 'Taco', image: 'brims/shape-taco.webp', imageFull: 'brims/shape-taco.webp', alt: 'Full taco brim shape' },
            { value: 'buckaroo', label: 'Buckaroo', image: 'brims/shape-buckaroo.svg', imageFull: 'brims/shape-buckaroo.svg', alt: 'Buckaroo brim shape' },
            { value: 'other', label: 'Other', alt: 'Custom brim shape - describe your preference' }
          ],
          brimSizes: [
            { value: '3', label: '3"' },
            { value: '3.25', label: '3\u00BC"' },
            { value: '3.5', label: '3\u00BD"' },
            { value: '3.75', label: '3\u00BE"' },
            { value: '4', label: '4"' },
            { value: '4.25', label: '4\u00BC"' },
            { value: '4.5', label: '4\u00BD"' },
            { value: '4.75', label: '4\u00BE"' },
            { value: '5', label: '5"' }
          ],
          ribbonColors: [
            { value: 'natural', label: 'Natural', color: '#c3af8a' },
            { value: 'khaki', label: 'Khaki', color: '#ab9673' },
            { value: 'coffee', label: 'Coffee', color: '#81573b' },
            { value: 'milk-chocolate', label: 'Milk Chocolate', color: '#6b4b34' },
            { value: 'brown', label: 'Brown', color: '#63554c' },
            { value: 'burgundy', label: 'Burgundy', color: '#642e3c' },
            { value: 'cranberry', label: 'Cranberry', color: '#a33244' },
            { value: 'red', label: 'Red', color: '#cd3a43' },
            { value: 'pink', label: 'Pink', color: '#e2abae' },
            { value: 'hot-pink', label: 'Hot Pink', color: '#e687a0' },
            { value: 'black', label: 'Black', color: '#3a3b3f' },
            { value: 'pewter', label: 'Pewter', color: '#717172' },
            { value: 'grey', label: 'Grey', color: '#9a9693' },
            { value: 'navy', label: 'Navy', color: '#343c4f' },
            { value: 'century-blue', label: 'Century Blue', color: '#27397c' },
            { value: 'blue', label: 'Blue', color: '#a4bad1' },
            { value: 'turquoise', label: 'Turquoise', color: '#1e96c5' },
            { value: 'navajo-turquoise', label: 'Navajo Turquoise', color: '#66b8c3' },
            { value: 'forest', label: 'Forest', color: '#2f5b4e' },
            { value: 'moss', label: 'Moss', color: '#757d4e' },
            { value: 'purple', label: 'Purple', color: '#753089' },
            { value: 'orchid', label: 'Orchid', color: '#bd9fc9' }
          ],
          pricing: {
            ribbon: { narrow: 45, wide: 45, double: 60 },
            boundEdge: { narrow: 40 },
            branding: { base: 20, perAdditionalLetter: 5, includedLetters: 3 }
          }
        },
        optionSets: this._buildEmbeddedOptionSets()
      };
    }

    _buildEmbeddedOptionSets() {
      const fullOptionSteps = [
            {
              id: 'customize',
              order: 1,
              title: 'Customize Your Hat?',
              subtitle: 'Choose to have your hat professionally shaped',
              required: true,
              type: 'buttonGroup',
              lineItemProperty: 'Hat Customized',
              options: [
                { value: 'yes', label: 'Yes, Shape My Hat', description: 'Professional shaping to your specifications', action: 'continue' },
                { value: 'no', label: 'No, Keep As-Is', description: 'Open crown, flat brim, sold as-is', action: 'skipToCart' }
              ],
              defaultValue: 'yes'
            },
            {
              id: 'shapeMethod', order: 2, title: 'How would you like to specify your hat shape?',
              subtitle: 'Show us a reference photo OR walk through our shape options.',
              required: true, type: 'buttonGroup', lineItemProperty: 'Shape Method',
              options: [
                { value: 'pickers', label: 'Choose From Our Options', description: 'Walk through crown shape, brim front, brim shape, etc.', action: 'continue' },
                { value: 'photo', label: 'Match a Reference Photo', description: "Upload photos and we'll shape your hat to match — no need to pick crown or brim style.", action: 'skipToPhotos' }
              ]
            },
            {
              id: 'headShape',
              order: 2,
              title: 'Head Shape',
              subtitle: 'Select your head shape for the best fit',
              helpGuide: {
                trigger: 'Not sure? Here\u2019s how to tell',
                intro: 'Think about how hats usually fit you:',
                items: [
                  { label: 'Extra Long Oval', text: 'Your hats are usually extra tight in front and back with a lot of room on the sides.' },
                  { label: 'Long Oval', text: 'You usually get a red mark on the front of your forehead with room on the temples.' },
                  { label: 'Oval', text: 'Your hats are tight on the sides with some room in the front or back. This is the most common shape.' },
                  { label: 'Round', text: 'Extreme to moderate pressure on the sides with moderate amount of gapping in the front or back. Hats straight off the shelf typically do not fit and you have to get them rounded out.' }
                ],
                tip: 'If you\u2019re still unsure, we highly recommend using our <a href="/products/free-perfect-fit-guide" target="_blank">Perfect Fit Guide</a>, which is complimentary with your hat purchase. It takes all the guessing out of the process by accurately capturing your head shape and measurements to ensure a perfect fit.'
              },
              required: true,
              type: 'buttonGroup',
              lineItemProperty: 'Head Shape',
              optionsRef: 'sharedLibraries.headShapes'
            },
            {
              id: 'crownShape',
              order: 3,
              title: 'Crown Shape',
              subtitle: '',
              required: true,
              type: 'thumbnailPicker',
              lineItemProperty: 'Crown Shape',
              showWhen: { shapeMethod: ['pickers'] },
              categories: [
                { id: 'traditional', name: 'Traditional Styles', optionsRef: 'sharedLibraries.crowns.traditional' },
                { id: 'specialty', name: 'Specialty Styles', optionsRef: 'sharedLibraries.crowns.specialty' }
              ],
              conditionalFields: [
                {
                  showWhen: { optionValue: ['brick-initial'] },
                  field: {
                    id: 'brickInitial',
                    type: 'textField',
                    label: 'Brick Initial Letter',
                    placeholder: 'Enter one letter',
                    maxLength: 1,
                    required: true,
                    lineItemProperty: 'Brick Initial'
                  }
                }
              ]
            },
            {
              id: 'brimStyle',
              order: 4,
              title: 'Brim Style',
              subtitle: '',
              required: true,
              type: 'multiField',
              fields: [
                { id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', optionsRef: 'sharedLibraries.brimFronts', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimFrontCustom', label: 'Describe Your Brim Front', type: 'textField', required: true, showWhen: { brimFront: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Front Detail', placeholder: 'e.g., 7" wide, 12" extra wide', maxLength: 100 },
                { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.brimShapes', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 },
                { id: 'brimSize', label: 'Brim Size', type: 'dropdown', required: true, lineItemProperty: 'Brim Size', optionsRef: 'sharedLibraries.brimSizes' }
              ]
            },
            {
              id: 'ribbon',
              order: 5,
              title: 'Ribbon & Trim',
              subtitle: 'Optional upgrades for your hat',
              required: false,
              type: 'multiField',
              fields: [
                {
                  id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
                  options: [
                    { value: 'none', label: 'No Ribbon', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 },
                    { value: 'wide', label: 'Wide 1\u00BD"', price: 45 },
                    { value: 'double', label: 'Double Ribbons', price: 60 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide', 'double'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
                { id: 'secondRibbonColor', label: 'Second Ribbon Color', type: 'swatchPicker', required: false, showWhen: { ribbonType: ['double'] }, lineItemProperty: 'Second Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
                {
                  id: 'boundEdge', label: 'Custom Bound Edge', type: 'buttonGroup', required: true, lineItemProperty: 'Bound Edge',
                  options: [
                    { value: 'none', label: 'No Change', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'boundEdgeColor', label: 'Bound Edge Color', type: 'swatchPicker', required: true, showWhen: { boundEdge: ['narrow'] }, lineItemProperty: 'Bound Edge Color', optionsRef: 'sharedLibraries.ribbonColors' }
              ]
            },
            {
              id: 'branding',
              order: 6,
              title: 'Personalization',
              subtitle: 'Add your initials to the hat',
              required: false,
              type: 'multiField',
              fields: [
                {
                  id: 'wantBranding', label: 'Do you want branding?', type: 'buttonGroup', required: true, lineItemProperty: 'Branding',
                  options: [
                    { value: 'no', label: 'No Branding' },
                    { value: 'yes', label: 'Yes, Add Branding' }
                  ],
                  defaultValue: 'no'
                },
                {
                  id: 'brandingText', label: 'Branding Letters', type: 'textField', required: true,
                  showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Text',
                  placeholder: 'Enter up to 6 letters', maxLength: 6,
                  helpText: 'First 3 letters: $20, Additional letters: $5 each',
                  pricing: { type: 'tiered', tiers: [{ from: 1, to: 3, price: 20 }, { from: 4, to: 6, pricePerUnit: 5 }] }
                },
                {
                  id: 'brandingPlacement', label: 'Branding Placement', type: 'dropdown', required: true,
                  showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Placement',
                  options: [
                    { value: 'back-left', label: 'Back Left Side' },
                    { value: 'back-middle', label: 'Back Middle' },
                    { value: 'back-right', label: 'Back Right Side' },
                    { value: 'other', label: 'Other (specify in notes)' }
                  ]
                },
                {
                  id: 'brandingConfirm', label: 'Confirm Branding', type: 'checkbox', required: true,
                  showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Confirmed',
                  checkboxLabel: 'I understand that branding is permanent and makes the hat non-returnable'
                }
              ]
            },
            {
              id: 'notesAndPhotos',
              order: 7,
              title: 'Notes & Reference Photos',
              subtitle: 'Share any special instructions or inspiration',
              required: false,
              type: 'multiField',
              fields: [
                { id: 'notes', label: 'Additional Notes', type: 'textArea', required: false, lineItemProperty: 'Notes', placeholder: 'Any special instructions or requests...', maxLength: 500 },
                { id: 'photos', label: 'Reference Photos', type: 'fileUpload', required: false, lineItemProperty: 'Reference Photos', maxFiles: 5, acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'], maxFileSizeMB: 10, helpText: 'Upload up to 5 photos showing your desired style' }
              ]
            },
            {
              id: 'confirmation',
              order: 8,
              title: 'Confirm Your Order',
              subtitle: 'Review your selections and confirm sizing',
              required: true,
              type: 'multiField',
              showSummary: true,
              fields: [
                {
                  id: 'sizeConfirmation', label: 'Confirm Hat Sizing & Shaping', type: 'radioGroup', required: true, lineItemProperty: 'Size Confirmation',
                  options: [
                    { value: 'confident', label: 'I am confident in my selections', description: 'I understand the hat will not be returnable once shaped.' },
                    { value: 'contact', label: 'Please contact me before processing', description: 'A team member will reach out to discuss your order.' },
                    { value: 'guide', label: 'I will provide a Perfect Fit Guide', description: 'I have or will complete the Perfect Fit Guide.' }
                  ]
                },
                {
                  id: 'requestFitGuide', label: 'Perfect Fit Guide', type: 'checkbox', required: false,
                  showWhen: { sizeConfirmation: ['guide'] }, lineItemProperty: 'Fit Guide Requested',
                  checkboxLabel: 'Mail me a Physical Perfect Fit Guide', helpText: 'We will mail you a fit guide to complete before shaping your hat.'
                }
              ]
            }
          ];

      // Helper: clone steps with a different default brim size and cap available sizes
      const allBrimSizes = [
        { value: '3', label: '3"' }, { value: '3.25', label: '3\u00BC"' },
        { value: '3.5', label: '3\u00BD"' }, { value: '3.75', label: '3\u00BE"' },
        { value: '4', label: '4"' }, { value: '4.25', label: '4\u00BC"' },
        { value: '4.5', label: '4\u00BD"' }, { value: '4.75', label: '4\u00BE"' },
        { value: '5', label: '5"' }
      ];
      const withDefaultBrim = (steps, defaultBrimValue) => {
        const maxNum = parseFloat(defaultBrimValue);
        const filteredSizes = allBrimSizes.filter(s => parseFloat(s.value) <= maxNum);
        return JSON.parse(JSON.stringify(steps)).map(step => {
          if (step.id === 'brimStyle' && step.fields) {
            const brimField = step.fields.find(f => f.id === 'brimSize');
            if (brimField) {
              brimField.defaultValue = defaultBrimValue;
              brimField.options = filteredSizes;
              delete brimField.optionsRef;
            }
          }
          return step;
        });
      };

      // Atwood-only — re-point the headShape step at the atwoodHeadShapes
      // library so customers see Round as a head-shape option.
      const withAtwoodHeadShape = (steps) => {
        return JSON.parse(JSON.stringify(steps)).map(step => {
          if (step.id === 'headShape') {
            step.optionsRef = 'sharedLibraries.atwoodHeadShapes';
          }
          return step;
        });
      };

      // Group B: Straw full shaping (crown + brim front/shape, ribbon, no brim size/edge/branding)
      const strawSteps = [
            {
              id: 'customize', order: 1, title: 'Customize Your Hat?',
              subtitle: 'Choose to have your hat professionally shaped',
              required: true, type: 'buttonGroup', lineItemProperty: 'Hat Customized',
              options: [
                { value: 'yes', label: 'Yes, Shape My Hat', description: 'Professional shaping to your specifications', action: 'continue' },
                { value: 'no', label: 'No, Keep As-Is', description: 'Open crown, flat brim, sold as-is', action: 'skipToCart' }
              ],
              defaultValue: 'yes'
            },
            {
              id: 'shapeMethod', order: 2, title: 'How would you like to specify your hat shape?',
              subtitle: 'Show us a reference photo OR walk through our shape options.',
              required: true, type: 'buttonGroup', lineItemProperty: 'Shape Method',
              options: [
                { value: 'pickers', label: 'Choose From Our Options', description: 'Walk through crown shape, brim front, brim shape, etc.', action: 'continue' },
                { value: 'photo', label: 'Match a Reference Photo', description: "Upload photos and we'll shape your hat to match — no need to pick crown or brim style.", action: 'skipToPhotos' }
              ]
            },
            {
              id: 'headShape', order: 3, title: 'Head Shape',
              subtitle: '',
              helpGuide: {
                trigger: 'Not sure? Here\u2019s how to tell',
                intro: 'Think about how hats usually fit you:',
                items: [
                  { label: 'Extra Long Oval', text: 'Your hats are usually extra tight in front and back with a lot of room on the sides.' },
                  { label: 'Long Oval', text: 'You usually get a red mark on the front of your forehead with room on the temples.' },
                  { label: 'Oval', text: 'Your hats are tight on the sides with some room in the front or back. This is the most common shape.' },
                  { label: 'Round', text: 'Extreme to moderate pressure on the sides with moderate amount of gapping in the front or back. Hats straight off the shelf typically do not fit and you have to get them rounded out.' }
                ],
                tip: 'If you\u2019re still unsure, we highly recommend using our <a href="/products/free-perfect-fit-guide" target="_blank">Perfect Fit Guide</a>, which is complimentary with your hat purchase. It takes all the guessing out of the process by accurately capturing your head shape and measurements to ensure a perfect fit.'
              },
              required: true, type: 'buttonGroup', lineItemProperty: 'Head Shape',
              optionsRef: 'sharedLibraries.headShapes'
            },
            {
              id: 'crownShape', order: 3, title: 'Crown Shape',
              subtitle: '',
              required: true, type: 'thumbnailPicker', lineItemProperty: 'Crown Shape',
              optionsRef: 'sharedLibraries.crowns.allFlat',
              showWhen: { shapeMethod: ['pickers'] }
            },
            {
              id: 'brimStyle', order: 4, title: 'Brim Style',
              subtitle: '',
              required: true, type: 'multiField',
              fields: [
                { id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', optionsRef: 'sharedLibraries.strawBrimFronts', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimFrontCustom', label: 'Describe Your Brim Front', type: 'textField', required: true, showWhen: { brimFront: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Front Detail', placeholder: 'e.g., 7" wide, 12" extra wide', maxLength: 100 },
                { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.strawBrimShapes', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 }
              ]
            },
            {
              id: 'ribbon', order: 5, title: 'Ribbon',
              subtitle: 'Optional upgrade for your hat',
              required: false, type: 'multiField',
              fields: [
                {
                  id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
                  options: [
                    { value: 'none', label: 'No Change', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 },
                    { value: 'wide', label: 'Wide 1\u00BD"', price: 45 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' }
              ]
            },
            {
              id: 'notesAndPhotos', order: 6, title: 'Notes & Reference Photos',
              subtitle: 'Share any special instructions or inspiration',
              required: false, type: 'multiField',
              fields: [
                { id: 'notes', label: 'Additional Notes', type: 'textArea', required: false, lineItemProperty: 'Notes', placeholder: 'Any special instructions or requests...', maxLength: 500 },
                { id: 'photos', label: 'Reference Photos', type: 'fileUpload', required: false, lineItemProperty: 'Reference Photos', maxFiles: 5, acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'], maxFileSizeMB: 10, helpText: 'Upload up to 5 photos showing your desired style' }
              ]
            },
            {
              id: 'confirmation', order: 7, title: 'Confirm Your Order',
              subtitle: 'Review your selections and confirm sizing',
              required: true, type: 'multiField', showSummary: true,
              fields: [
                {
                  id: 'sizeConfirmation', label: 'Confirm Hat Sizing & Shaping', type: 'radioGroup', required: true, lineItemProperty: 'Size Confirmation',
                  options: [
                    { value: 'confident', label: 'I am confident in my selections', description: 'I understand the hat will not be returnable once shaped.' },
                    { value: 'contact', label: 'Please contact me before processing', description: 'A team member will reach out to discuss your order.' },
                    { value: 'guide', label: 'I will provide a Perfect Fit Guide', description: 'I have or will complete the Perfect Fit Guide.' }
                  ]
                },
                {
                  id: 'requestFitGuide', label: 'Perfect Fit Guide', type: 'checkbox', required: false,
                  showWhen: { sizeConfirmation: ['guide'] }, lineItemProperty: 'Fit Guide Requested',
                  checkboxLabel: 'Mail me a Physical Perfect Fit Guide', helpText: 'We will mail you a fit guide to complete before shaping your hat.'
                }
              ]
            }
          ];

      // Group C: No Crown — felt variant (has double ribbon, full brim libraries)
      const feltNoCrownSteps = [
            {
              id: 'customize', order: 1, title: 'Customize Your Hat?',
              subtitle: 'Choose to have your hat professionally shaped',
              required: true, type: 'buttonGroup', lineItemProperty: 'Hat Customized',
              options: [
                { value: 'yes', label: 'Yes, Shape My Hat', description: 'Professional shaping to your specifications', action: 'continue' },
                { value: 'no', label: 'No, Keep As-Is', description: 'Open crown, flat brim, sold as-is', action: 'skipToCart' }
              ],
              defaultValue: 'yes'
            },
            {
              id: 'shapeMethod', order: 2, title: 'How would you like to specify your hat shape?',
              subtitle: 'Show us a reference photo OR walk through our shape options.',
              required: true, type: 'buttonGroup', lineItemProperty: 'Shape Method',
              options: [
                { value: 'pickers', label: 'Choose From Our Options', description: 'Walk through crown shape, brim front, brim shape, etc.', action: 'continue' },
                { value: 'photo', label: 'Match a Reference Photo', description: "Upload photos and we'll shape your hat to match — no need to pick crown or brim style.", action: 'skipToPhotos' }
              ]
            },
            {
              id: 'headShape', order: 3, title: 'Head Shape',
              subtitle: '',
              helpGuide: {
                trigger: 'Not sure? Here\u2019s how to tell',
                intro: 'Think about how hats usually fit you:',
                items: [
                  { label: 'Extra Long Oval', text: 'Your hats are usually extra tight in front and back with a lot of room on the sides.' },
                  { label: 'Long Oval', text: 'You usually get a red mark on the front of your forehead with room on the temples.' },
                  { label: 'Oval', text: 'Your hats are tight on the sides with some room in the front or back. This is the most common shape.' },
                  { label: 'Round', text: 'Extreme to moderate pressure on the sides with moderate amount of gapping in the front or back. Hats straight off the shelf typically do not fit and you have to get them rounded out.' }
                ],
                tip: 'If you\u2019re still unsure, we highly recommend using our <a href="/products/free-perfect-fit-guide" target="_blank">Perfect Fit Guide</a>, which is complimentary with your hat purchase. It takes all the guessing out of the process by accurately capturing your head shape and measurements to ensure a perfect fit.'
              },
              required: true, type: 'buttonGroup', lineItemProperty: 'Head Shape',
              optionsRef: 'sharedLibraries.headShapes'
            },
            {
              id: 'brimStyle', order: 3, title: 'Brim Style',
              subtitle: '',
              required: true, type: 'multiField',
              fields: [
                { id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', optionsRef: 'sharedLibraries.brimFronts', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimFrontCustom', label: 'Describe Your Brim Front', type: 'textField', required: true, showWhen: { brimFront: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Front Detail', placeholder: 'e.g., 7" wide, 12" extra wide', maxLength: 100 },
                { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.brimShapes', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 }
              ]
            },
            {
              id: 'ribbon', order: 4, title: 'Ribbon',
              subtitle: 'Optional upgrade for your hat',
              required: false, type: 'multiField',
              fields: [
                {
                  id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
                  options: [
                    { value: 'none', label: 'No Ribbon', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 },
                    { value: 'wide', label: 'Wide 1\u00BD"', price: 45 },
                    { value: 'double', label: 'Double Ribbons', price: 60 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide', 'double'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
                { id: 'secondRibbonColor', label: 'Second Ribbon Color', type: 'swatchPicker', required: false, showWhen: { ribbonType: ['double'] }, lineItemProperty: 'Second Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' }
              ]
            },
            {
              id: 'notesAndPhotos', order: 5, title: 'Notes & Reference Photos',
              subtitle: 'Share any special instructions or inspiration',
              required: false, type: 'multiField',
              fields: [
                { id: 'notes', label: 'Additional Notes', type: 'textArea', required: false, lineItemProperty: 'Notes', placeholder: 'Any special instructions or requests...', maxLength: 500 },
                { id: 'photos', label: 'Reference Photos', type: 'fileUpload', required: false, lineItemProperty: 'Reference Photos', maxFiles: 5, acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'], maxFileSizeMB: 10, helpText: 'Upload up to 5 photos showing your desired style' }
              ]
            },
            {
              id: 'confirmation', order: 6, title: 'Confirm Your Order',
              subtitle: 'Review your selections and confirm sizing',
              required: true, type: 'multiField', showSummary: true,
              fields: [
                {
                  id: 'sizeConfirmation', label: 'Confirm Hat Sizing & Shaping', type: 'radioGroup', required: true, lineItemProperty: 'Size Confirmation',
                  options: [
                    { value: 'confident', label: 'I am confident in my selections', description: 'I understand the hat will not be returnable once shaped.' },
                    { value: 'contact', label: 'Please contact me before processing', description: 'A team member will reach out to discuss your order.' },
                    { value: 'guide', label: 'I will provide a Perfect Fit Guide', description: 'I have or will complete the Perfect Fit Guide.' }
                  ]
                },
                {
                  id: 'requestFitGuide', label: 'Perfect Fit Guide', type: 'checkbox', required: false,
                  showWhen: { sizeConfirmation: ['guide'] }, lineItemProperty: 'Fit Guide Requested',
                  checkboxLabel: 'Mail me a Physical Perfect Fit Guide', helpText: 'We will mail you a fit guide to complete before shaping your hat.'
                }
              ]
            }
          ];

      // Group C: No Crown — straw variant (single ribbon only, straw brim libraries)
      const strawNoCrownSteps = [
            {
              id: 'customize', order: 1, title: 'Customize Your Hat?',
              subtitle: 'Choose to have your hat professionally shaped',
              required: true, type: 'buttonGroup', lineItemProperty: 'Hat Customized',
              options: [
                { value: 'yes', label: 'Yes, Shape My Hat', description: 'Professional shaping to your specifications', action: 'continue' },
                { value: 'no', label: 'No, Keep As-Is', description: 'Open crown, flat brim, sold as-is', action: 'skipToCart' }
              ],
              defaultValue: 'yes'
            },
            {
              id: 'shapeMethod', order: 2, title: 'How would you like to specify your hat shape?',
              subtitle: 'Show us a reference photo OR walk through our shape options.',
              required: true, type: 'buttonGroup', lineItemProperty: 'Shape Method',
              options: [
                { value: 'pickers', label: 'Choose From Our Options', description: 'Walk through crown shape, brim front, brim shape, etc.', action: 'continue' },
                { value: 'photo', label: 'Match a Reference Photo', description: "Upload photos and we'll shape your hat to match — no need to pick crown or brim style.", action: 'skipToPhotos' }
              ]
            },
            {
              id: 'headShape', order: 3, title: 'Head Shape',
              subtitle: '',
              helpGuide: {
                trigger: 'Not sure? Here\u2019s how to tell',
                intro: 'Think about how hats usually fit you:',
                items: [
                  { label: 'Extra Long Oval', text: 'Your hats are usually extra tight in front and back with a lot of room on the sides.' },
                  { label: 'Long Oval', text: 'You usually get a red mark on the front of your forehead with room on the temples.' },
                  { label: 'Oval', text: 'Your hats are tight on the sides with some room in the front or back. This is the most common shape.' },
                  { label: 'Round', text: 'Extreme to moderate pressure on the sides with moderate amount of gapping in the front or back. Hats straight off the shelf typically do not fit and you have to get them rounded out.' }
                ],
                tip: 'If you\u2019re still unsure, we highly recommend using our <a href="/products/free-perfect-fit-guide" target="_blank">Perfect Fit Guide</a>, which is complimentary with your hat purchase. It takes all the guessing out of the process by accurately capturing your head shape and measurements to ensure a perfect fit.'
              },
              required: true, type: 'buttonGroup', lineItemProperty: 'Head Shape',
              optionsRef: 'sharedLibraries.headShapes'
            },
            {
              id: 'brimStyle', order: 3, title: 'Brim Style',
              subtitle: '',
              required: true, type: 'multiField',
              fields: [
                { id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', optionsRef: 'sharedLibraries.strawBrimFronts', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimFrontCustom', label: 'Describe Your Brim Front', type: 'textField', required: true, showWhen: { brimFront: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Front Detail', placeholder: 'e.g., 7" wide, 12" extra wide', maxLength: 100 },
                { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.strawBrimShapes', showWhen: { shapeMethod: ['pickers'] } },
                { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 }
              ]
            },
            {
              id: 'ribbon', order: 4, title: 'Ribbon',
              subtitle: 'Optional upgrade for your hat',
              required: false, type: 'multiField',
              fields: [
                {
                  id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
                  options: [
                    { value: 'none', label: 'No Change', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 },
                    { value: 'wide', label: 'Wide 1\u00BD"', price: 45 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' }
              ]
            },
            {
              id: 'notesAndPhotos', order: 5, title: 'Notes & Reference Photos',
              subtitle: 'Share any special instructions or inspiration',
              required: false, type: 'multiField',
              fields: [
                { id: 'notes', label: 'Additional Notes', type: 'textArea', required: false, lineItemProperty: 'Notes', placeholder: 'Any special instructions or requests...', maxLength: 500 },
                { id: 'photos', label: 'Reference Photos', type: 'fileUpload', required: false, lineItemProperty: 'Reference Photos', maxFiles: 5, acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'], maxFileSizeMB: 10, helpText: 'Upload up to 5 photos showing your desired style' }
              ]
            },
            {
              id: 'confirmation', order: 6, title: 'Confirm Your Order',
              subtitle: 'Review your selections and confirm sizing',
              required: true, type: 'multiField', showSummary: true,
              fields: [
                {
                  id: 'sizeConfirmation', label: 'Confirm Hat Sizing & Shaping', type: 'radioGroup', required: true, lineItemProperty: 'Size Confirmation',
                  options: [
                    { value: 'confident', label: 'I am confident in my selections', description: 'I understand the hat will not be returnable once shaped.' },
                    { value: 'contact', label: 'Please contact me before processing', description: 'A team member will reach out to discuss your order.' },
                    { value: 'guide', label: 'I will provide a Perfect Fit Guide', description: 'I have or will complete the Perfect Fit Guide.' }
                  ]
                },
                {
                  id: 'requestFitGuide', label: 'Perfect Fit Guide', type: 'checkbox', required: false,
                  showWhen: { sizeConfirmation: ['guide'] }, lineItemProperty: 'Fit Guide Requested',
                  checkboxLabel: 'Mail me a Physical Perfect Fit Guide', helpText: 'We will mail you a fit guide to complete before shaping your hat.'
                }
              ]
            }
          ];

      // Group D1: Pre-shaped minimal — ribbon only (no head shape, no crown, no brim)
      const preShapedRibbonSteps = [
            {
              id: 'customize', order: 1, title: 'Customize Your Hat?',
              subtitle: 'Choose to upgrade your ribbon',
              required: true, type: 'buttonGroup', lineItemProperty: 'Hat Customized',
              options: [
                { value: 'yes', label: 'Yes, Customize', description: 'Choose a custom ribbon for your hat', action: 'continue' },
                { value: 'no', label: 'No, Keep As-Is', description: 'Keep the hat as it comes', action: 'skipToCart' }
              ],
              defaultValue: 'yes'
            },
            {
              id: 'ribbon', order: 2, title: 'Ribbon',
              subtitle: 'Optional upgrade for your hat',
              required: false, type: 'multiField',
              fields: [
                {
                  id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
                  options: [
                    { value: 'none', label: 'No Change', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 },
                    { value: 'wide', label: 'Wide 1\u00BD"', price: 45 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' }
              ]
            },
            {
              id: 'notesAndPhotos', order: 3, title: 'Notes & Reference Photos',
              subtitle: 'Share any special instructions or inspiration',
              required: false, type: 'multiField',
              fields: [
                { id: 'notes', label: 'Additional Notes', type: 'textArea', required: false, lineItemProperty: 'Notes', placeholder: 'Any special instructions or requests...', maxLength: 500 },
                { id: 'photos', label: 'Reference Photos', type: 'fileUpload', required: false, lineItemProperty: 'Reference Photos', maxFiles: 5, acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'], maxFileSizeMB: 10, helpText: 'Upload up to 5 photos showing your desired style' }
              ]
            },
            {
              id: 'confirmation', order: 4, title: 'Confirm Your Order',
              subtitle: 'Review your selections and confirm sizing',
              required: true, type: 'multiField', showSummary: true,
              fields: [
                {
                  id: 'sizeConfirmation', label: 'Confirm Hat Sizing & Shaping', type: 'radioGroup', required: true, lineItemProperty: 'Size Confirmation',
                  options: [
                    { value: 'confident', label: 'I am confident in my selections', description: 'I understand the hat will not be returnable once shaped.' },
                    { value: 'contact', label: 'Please contact me before processing', description: 'A team member will reach out to discuss your order.' },
                    { value: 'guide', label: 'I will provide a Perfect Fit Guide', description: 'I have or will complete the Perfect Fit Guide.' }
                  ]
                },
                {
                  id: 'requestFitGuide', label: 'Perfect Fit Guide', type: 'checkbox', required: false,
                  showWhen: { sizeConfirmation: ['guide'] }, lineItemProperty: 'Fit Guide Requested',
                  checkboxLabel: 'Mail me a Physical Perfect Fit Guide', helpText: 'We will mail you a fit guide to complete before shaping your hat.'
                }
              ]
            }
          ];

      // Group D2: Pre-shaped with brim size + ribbon + bound edge (no head shape, no crown, no brim front/shape)
      const preShapedBrimSteps = [
            {
              id: 'customize', order: 1, title: 'Customize Your Hat?',
              subtitle: 'Choose to customize your pre-shaped hat',
              required: true, type: 'buttonGroup', lineItemProperty: 'Hat Customized',
              options: [
                { value: 'yes', label: 'Yes, Customize', description: 'Adjust brim size, ribbon, and edge options', action: 'continue' },
                { value: 'no', label: 'No, Keep As-Is', description: 'Keep the hat as it comes', action: 'skipToCart' }
              ],
              defaultValue: 'yes'
            },
            {
              id: 'brimStyle', order: 2, title: 'Brim Size',
              subtitle: 'Select your preferred brim size',
              required: true, type: 'multiField',
              fields: [
                {
                  id: 'brimSize', label: 'Brim Size', type: 'dropdown', required: true, lineItemProperty: 'Brim Size',
                  optionsRef: 'sharedLibraries.brimSizes',
                  defaultValue: '4.5'
                }
              ]
            },
            {
              id: 'ribbon', order: 3, title: 'Ribbon & Edge',
              subtitle: 'Optional upgrades for your hat',
              required: false, type: 'multiField',
              fields: [
                {
                  id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
                  options: [
                    { value: 'none', label: 'No Ribbon', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 },
                    { value: 'wide', label: 'Wide 1\u00BD"', price: 45 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
                {
                  id: 'boundEdge', label: 'Custom Bound Edge', type: 'buttonGroup', required: true, lineItemProperty: 'Bound Edge',
                  options: [
                    { value: 'none', label: 'No Change', price: 0 },
                    { value: 'narrow', label: 'Narrow 5/8"', price: 45 }
                  ],
                  defaultValue: 'none'
                },
                { id: 'boundEdgeColor', label: 'Bound Edge Color', type: 'swatchPicker', required: true, showWhen: { boundEdge: ['narrow'] }, lineItemProperty: 'Bound Edge Color', optionsRef: 'sharedLibraries.ribbonColors' }
              ]
            },
            {
              id: 'notesAndPhotos', order: 4, title: 'Notes & Reference Photos',
              subtitle: 'Share any special instructions or inspiration',
              required: false, type: 'multiField',
              fields: [
                { id: 'notes', label: 'Additional Notes', type: 'textArea', required: false, lineItemProperty: 'Notes', placeholder: 'Any special instructions or requests...', maxLength: 500 },
                { id: 'photos', label: 'Reference Photos', type: 'fileUpload', required: false, lineItemProperty: 'Reference Photos', maxFiles: 5, acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'], maxFileSizeMB: 10, helpText: 'Upload up to 5 photos showing your desired style' }
              ]
            },
            {
              id: 'confirmation', order: 5, title: 'Confirm Your Order',
              subtitle: 'Review your selections and confirm sizing',
              required: true, type: 'multiField', showSummary: true,
              fields: [
                {
                  id: 'sizeConfirmation', label: 'Confirm Hat Sizing & Shaping', type: 'radioGroup', required: true, lineItemProperty: 'Size Confirmation',
                  options: [
                    { value: 'confident', label: 'I am confident in my selections', description: 'I understand the hat will not be returnable once shaped.' },
                    { value: 'contact', label: 'Please contact me before processing', description: 'A team member will reach out to discuss your order.' },
                    { value: 'guide', label: 'I will provide a Perfect Fit Guide', description: 'I have or will complete the Perfect Fit Guide.' }
                  ]
                },
                {
                  id: 'requestFitGuide', label: 'Perfect Fit Guide', type: 'checkbox', required: false,
                  showWhen: { sizeConfirmation: ['guide'] }, lineItemProperty: 'Fit Guide Requested',
                  checkboxLabel: 'Mail me a Physical Perfect Fit Guide', helpText: 'We will mail you a fit guide to complete before shaping your hat.'
                }
              ]
            }
          ];

      // --- Shared step fragments for Group E ---
      const sharedHeadShapeStep = (order) => ({
        id: 'headShape', order, title: 'Head Shape', subtitle: '',
        helpGuide: {
          trigger: 'Not sure? Here\u2019s how to tell',
          intro: 'Think about how hats usually fit you:',
          items: [
            { label: 'Extra Long Oval', text: 'Your hats are usually extra tight in front and back with a lot of room on the sides.' },
            { label: 'Long Oval', text: 'You usually get a red mark on the front of your forehead with room on the temples.' },
            { label: 'Oval', text: 'Your hats are tight on the sides with some room in the front or back. This is the most common shape.' },
            { label: 'Round', text: 'Extreme to moderate pressure on the sides with moderate amount of gapping in the front or back. Hats straight off the shelf typically do not fit and you have to get them rounded out.' }
          ],
          tip: 'If you\u2019re still unsure, we highly recommend using our <a href="/products/free-perfect-fit-guide" target="_blank">Perfect Fit Guide</a>, which is complimentary with your hat purchase. It takes all the guessing out of the process by accurately capturing your head shape and measurements to ensure a perfect fit.'
        },
        required: true, type: 'buttonGroup', lineItemProperty: 'Head Shape', optionsRef: 'sharedLibraries.headShapes'
      });
      const sharedCustomizeStep = (order, subtitle) => ({
        id: 'customize', order, title: 'Customize Your Hat?', subtitle: subtitle || 'Choose to have your hat professionally shaped',
        required: true, type: 'buttonGroup', lineItemProperty: 'Hat Customized',
        options: [
          { value: 'yes', label: 'Yes, Shape My Hat', description: 'Professional shaping to your specifications', action: 'continue' },
          { value: 'no', label: 'No, Keep As-Is', description: 'Keep the hat as it comes', action: 'skipToCart' }
        ], defaultValue: 'yes'
      });
      // Shape Method — see v1 for full explanation.
      const sharedShapeMethodStep = (order) => ({
        id: 'shapeMethod', order, title: 'How would you like to specify your hat shape?',
        subtitle: 'Show us a reference photo OR walk through our shape options.',
        required: true, type: 'buttonGroup', lineItemProperty: 'Shape Method',
        options: [
          { value: 'pickers', label: 'Choose From Our Options', description: 'Walk through crown shape, brim front, brim shape, etc.', action: 'continue' },
          { value: 'photo', label: 'Match a Reference Photo', description: "Upload photos and we'll shape your hat to match — no need to pick crown or brim style.", action: 'skipToPhotos' }
        ]
      });
      const sharedNotesStep = (order) => ({
        id: 'notesAndPhotos', order, title: 'Notes & Reference Photos', subtitle: 'Share any special instructions or inspiration',
        required: false, type: 'multiField',
        fields: [
          { id: 'notes', label: 'Additional Notes', type: 'textArea', required: false, lineItemProperty: 'Notes', placeholder: 'Any special instructions or requests...', maxLength: 500 },
          { id: 'photos', label: 'Reference Photos', type: 'fileUpload', required: false, lineItemProperty: 'Reference Photos', maxFiles: 5, acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'], maxFileSizeMB: 10, helpText: 'Upload up to 5 photos showing your desired style' }
        ]
      });
      const sharedConfirmStep = (order) => ({
        id: 'confirmation', order, title: 'Confirm Your Order', subtitle: 'Review your selections and confirm sizing',
        required: true, type: 'multiField', showSummary: true,
        fields: [
          { id: 'sizeConfirmation', label: 'Confirm Hat Sizing & Shaping', type: 'radioGroup', required: true, lineItemProperty: 'Size Confirmation',
            options: [
              { value: 'confident', label: 'I am confident in my selections', description: 'I understand the hat will not be returnable once shaped.' },
              { value: 'contact', label: 'Please contact me before processing', description: 'A team member will reach out to discuss your order.' },
              { value: 'guide', label: 'I will provide a Perfect Fit Guide', description: 'I have or will complete the Perfect Fit Guide.' }
            ] },
          { id: 'requestFitGuide', label: 'Perfect Fit Guide', type: 'checkbox', required: false,
            showWhen: { sizeConfirmation: ['guide'] }, lineItemProperty: 'Fit Guide Requested',
            checkboxLabel: 'Mail me a Physical Perfect Fit Guide', helpText: 'We will mail you a fit guide to complete before shaping your hat.' }
        ]
      });

      // E1: Twister 3x Wool — full options minus crown (BSS 275278)
      const twister3xWoolSteps = [
        sharedCustomizeStep(1),
        sharedShapeMethodStep(2),
        sharedHeadShapeStep(3),
        {
          id: 'brimStyle', order: 3, title: 'Brim Style', subtitle: '', required: true, type: 'multiField',
          fields: [
            { id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', optionsRef: 'sharedLibraries.brimFronts', showWhen: { shapeMethod: ['pickers'] } },
            { id: 'brimFrontCustom', label: 'Describe Your Brim Front', type: 'textField', required: true, showWhen: { brimFront: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Front Detail', placeholder: 'e.g., 7" wide, 12" extra wide', maxLength: 100 },
            { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.brimShapes', showWhen: { shapeMethod: ['pickers'] } },
            { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 },
            { id: 'brimSize', label: 'Brim Size', type: 'dropdown', required: true, lineItemProperty: 'Brim Size', optionsRef: 'sharedLibraries.brimSizes', defaultValue: '4.5' }
          ]
        },
        {
          id: 'ribbon', order: 4, title: 'Ribbon & Edge', subtitle: 'Optional upgrades for your hat', required: false, type: 'multiField',
          fields: [
            { id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
              options: [ { value: 'none', label: 'No Ribbon', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 }, { value: 'wide', label: 'Wide 1\u00BD"', price: 45 }, { value: 'double', label: 'Double Ribbons', price: 60 } ], defaultValue: 'none' },
            { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide', 'double'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
            { id: 'secondRibbonColor', label: 'Second Ribbon Color', type: 'swatchPicker', required: false, showWhen: { ribbonType: ['double'] }, lineItemProperty: 'Second Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
            { id: 'boundEdge', label: 'Custom Bound Edge', type: 'buttonGroup', required: true, lineItemProperty: 'Bound Edge',
              options: [ { value: 'none', label: 'No Change', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 } ], defaultValue: 'none' },
            { id: 'boundEdgeColor', label: 'Bound Edge Color', type: 'swatchPicker', required: true, showWhen: { boundEdge: ['narrow'] }, lineItemProperty: 'Bound Edge Color', optionsRef: 'sharedLibraries.ribbonColors' }
          ]
        },
        {
          id: 'branding', order: 5, title: 'Personalization', subtitle: 'Add your initials to the hat', required: false, type: 'multiField',
          fields: [
            { id: 'wantBranding', label: 'Do you want branding?', type: 'buttonGroup', required: true, lineItemProperty: 'Branding',
              options: [ { value: 'no', label: 'No Branding' }, { value: 'yes', label: 'Yes, Add Branding' } ], defaultValue: 'no' },
            { id: 'brandingText', label: 'Branding Letters', type: 'textField', required: true, showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Text',
              placeholder: 'Enter up to 6 letters', maxLength: 6, helpText: 'First 3 letters: $20, Additional letters: $5 each',
              pricing: { type: 'tiered', tiers: [{ from: 1, to: 3, price: 20 }, { from: 4, to: 6, pricePerUnit: 5 }] } },
            { id: 'brandingPlacement', label: 'Branding Placement', type: 'dropdown', required: true, showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Placement',
              options: [ { value: 'back-left', label: 'Back Left Side' }, { value: 'back-middle', label: 'Back Middle' }, { value: 'back-right', label: 'Back Right Side' }, { value: 'other', label: 'Other (specify in notes)' } ] },
            { id: 'brandingConfirm', label: 'Confirm Branding', type: 'checkbox', required: true, showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Confirmed',
              checkboxLabel: 'I understand that branding is permanent and makes the hat non-returnable' }
          ]
        },
        sharedNotesStep(6),
        sharedConfirmStep(7)
      ];

      // E2: Stetson — head shape + full brim + ribbon + edge, no crown/branding (BSS 277047)
      const stetsonSteps = [
        sharedCustomizeStep(1),
        sharedShapeMethodStep(2),
        sharedHeadShapeStep(3),
        {
          id: 'brimStyle', order: 3, title: 'Brim Style', subtitle: '', required: true, type: 'multiField',
          fields: [
            { id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', optionsRef: 'sharedLibraries.brimFronts', showWhen: { shapeMethod: ['pickers'] } },
            { id: 'brimFrontCustom', label: 'Describe Your Brim Front', type: 'textField', required: true, showWhen: { brimFront: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Front Detail', placeholder: 'e.g., 7" wide, 12" extra wide', maxLength: 100 },
            { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.brimShapes', showWhen: { shapeMethod: ['pickers'] } },
            { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 },
            { id: 'brimSize', label: 'Brim Size', type: 'dropdown', required: true, lineItemProperty: 'Brim Size', optionsRef: 'sharedLibraries.brimSizes', defaultValue: '4.5' }
          ]
        },
        {
          id: 'ribbon', order: 4, title: 'Ribbon & Edge', subtitle: 'Optional upgrades for your hat', required: false, type: 'multiField',
          fields: [
            { id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
              options: [ { value: 'none', label: 'No Ribbon', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 }, { value: 'wide', label: 'Wide 1\u00BD"', price: 45 } ], defaultValue: 'none' },
            { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
            { id: 'boundEdge', label: 'Custom Bound Edge', type: 'buttonGroup', required: true, lineItemProperty: 'Bound Edge',
              options: [ { value: 'none', label: 'No Change', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 } ], defaultValue: 'none' },
            { id: 'boundEdgeColor', label: 'Bound Edge Color', type: 'swatchPicker', required: true, showWhen: { boundEdge: ['narrow'] }, lineItemProperty: 'Bound Edge Color', optionsRef: 'sharedLibraries.ribbonColors' }
          ]
        },
        sharedNotesStep(5),
        sharedConfirmStep(6)
      ];

      // E3: Serratelli Wool — full shaping, no add-ons (BSS 274999)
      const serratelliSteps = [
        sharedCustomizeStep(1),
        sharedShapeMethodStep(2),
        sharedHeadShapeStep(3),
        { id: 'crownShape', order: 3, title: 'Crown Shape', subtitle: '', required: true, type: 'thumbnailPicker', lineItemProperty: 'Crown Shape', optionsRef: 'sharedLibraries.crowns', showWhen: { shapeMethod: ['pickers'] } },
        {
          id: 'brimStyle', order: 4, title: 'Brim Style', subtitle: '', required: true, type: 'multiField',
          fields: [
            { id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', optionsRef: 'sharedLibraries.brimFronts', showWhen: { shapeMethod: ['pickers'] } },
            { id: 'brimFrontCustom', label: 'Describe Your Brim Front', type: 'textField', required: true, showWhen: { brimFront: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Front Detail', placeholder: 'e.g., 7" wide, 12" extra wide', maxLength: 100 },
            { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.brimShapes', showWhen: { shapeMethod: ['pickers'] } },
            { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 },
            { id: 'brimSize', label: 'Brim Size', type: 'dropdown', required: true, lineItemProperty: 'Brim Size', optionsRef: 'sharedLibraries.brimSizes', defaultValue: '4.5' }
          ]
        },
        sharedNotesStep(5),
        sharedConfirmStep(6)
      ];

      // E4: Bailey Easton — crown + brim shape/size + ribbon + edge, NO brim front (BSS 275001)
      const baileyEastonSteps = [
        sharedCustomizeStep(1),
        sharedShapeMethodStep(2),
        sharedHeadShapeStep(3),
        { id: 'crownShape', order: 3, title: 'Crown Shape', subtitle: '', required: true, type: 'thumbnailPicker', lineItemProperty: 'Crown Shape', optionsRef: 'sharedLibraries.crowns', showWhen: { shapeMethod: ['pickers'] } },
        {
          id: 'brimStyle', order: 4, title: 'Brim Style', subtitle: '', required: true, type: 'multiField',
          fields: [
            { id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', optionsRef: 'sharedLibraries.brimShapes', showWhen: { shapeMethod: ['pickers'] } },
            { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'], shapeMethod: ['pickers'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 },
            { id: 'brimSize', label: 'Brim Size', type: 'dropdown', required: true, lineItemProperty: 'Brim Size', optionsRef: 'sharedLibraries.brimSizes', defaultValue: '4.5' }
          ]
        },
        {
          id: 'ribbon', order: 5, title: 'Ribbon & Edge', subtitle: 'Optional upgrades for your hat', required: false, type: 'multiField',
          fields: [
            { id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
              options: [ { value: 'none', label: 'No Ribbon', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 }, { value: 'wide', label: 'Wide 1\u00BD"', price: 45 } ], defaultValue: 'none' },
            { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
            { id: 'boundEdge', label: 'Custom Bound Edge', type: 'buttonGroup', required: true, lineItemProperty: 'Bound Edge',
              options: [ { value: 'none', label: 'No Change', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 } ], defaultValue: 'none' },
            { id: 'boundEdgeColor', label: 'Bound Edge Color', type: 'swatchPicker', required: true, showWhen: { boundEdge: ['narrow'] }, lineItemProperty: 'Bound Edge Color', optionsRef: 'sharedLibraries.ribbonColors' }
          ]
        },
        sharedNotesStep(6),
        sharedConfirmStep(7)
      ];

      // E5: Straw Pre-creased Limited — head shape + limited crown + limited brim + ribbon w/ 2nd color (BSS 277349)
      const strawLimitedSteps = [
        sharedCustomizeStep(1),
        {
          id: 'headShape', order: 2, title: 'Head Shape',
          subtitle: '',
          helpGuide: {
            trigger: 'Not sure? Here\u2019s how to tell',
            intro: 'Think about how hats usually fit you:',
            items: [
              { label: 'Extra Long Oval', text: 'Your hats are usually extra tight in front and back with a lot of room on the sides.' },
              { label: 'Long Oval', text: 'You usually get a red mark on the front of your forehead with room on the temples.' },
              { label: 'Oval', text: 'Your hats are tight on the sides with some room in the front or back. This is the most common shape.' },
              { label: 'Round', text: 'Extreme to moderate pressure on the sides with moderate amount of gapping in the front or back. Hats straight off the shelf typically do not fit and you have to get them rounded out.' }
            ],
            tip: 'If you\u2019re still unsure, we highly recommend using our <a href="/products/free-perfect-fit-guide" target="_blank">Perfect Fit Guide</a>, which is complimentary with your hat purchase. It takes all the guessing out of the process by accurately capturing your head shape and measurements to ensure a perfect fit.'
          },
          required: true, type: 'buttonGroup', lineItemProperty: 'Head Shape',
          optionsRef: 'sharedLibraries.headShapes'
        },
        {
          id: 'crownShape', order: 3, title: 'Crown Shape', subtitle: 'This hat comes pre-creased — choose from our most popular crown styles to complement its look.', required: true, type: 'thumbnailPicker', lineItemProperty: 'Crown Shape', showWhen: { shapeMethod: ['pickers'] },
          options: [
            { value: 'minick',       label: 'Minick',       image: 'crowns/minick.svg',          alt: 'Minick crown style' },
            { value: 'cattleman',    label: 'Cattleman',    image: 'crowns/cattleman.webp',       alt: 'Cattleman - classic western style' },
            { value: 'cattleman-low',label: 'Low Cattleman',image: 'crowns/low-cattleman.webp',   alt: 'Low Cattleman style' }
          ]
        },
        {
          id: 'brimStyle', order: 4, title: 'Brim Style', subtitle: 'Select your brim front and shape to complete the look.', required: true, type: 'multiField',
          fields: [
            {
              id: 'brimFront', label: 'Brim Front', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Front', showWhen: { shapeMethod: ['pickers'] },
              options: [
                { value: 'pointed',  label: 'Pointed', image: 'brims/front-pointed.webp', alt: 'Pointed brim front' },
                { value: 'round',    label: 'Round',   image: 'brims/front-round.svg',    alt: 'Rounded brim front' },
                { value: 'square-6', label: 'Square',  image: 'brims/front-square.svg',   alt: 'Square brim front' },
                { value: 'wide-8',   label: 'Wide',    image: 'brims/front-wide.webp',    alt: 'Wide brim front' }
              ]
            },
            {
              id: 'brimShape', label: 'Brim Shape', type: 'thumbnailPicker', required: true, lineItemProperty: 'Brim Shape', showWhen: { shapeMethod: ['pickers'] },
              options: [
                { value: 'low',          label: 'Low',          image: 'brims/shape-low.webp',          alt: 'Low brim shape' },
                { value: 'george',       label: 'George',       image: 'brims/shape-george.svg',        alt: 'George Strait style brim' },
                { value: 'relaxed-taco', label: 'Relaxed Taco', image: 'brims/shape-relaxed-taco.webp', alt: 'Relaxed taco brim shape' },
                { value: 'half-taco',    label: 'Half Taco',    image: 'brims/shape-half-taco.webp',    alt: 'Half taco brim shape' },
                { value: 'taco',         label: 'Taco',         image: 'brims/shape-taco.webp',         alt: 'Full taco brim shape' },
                { value: 'other',        label: 'Other',                                                alt: 'Custom brim shape - describe your preference' }
                // "retro" and "come-and-go" removed 2026-04-28
              ]
            },
            { id: 'brimShapeCustom', label: 'Describe Your Brim Shape', type: 'textField', required: true, showWhen: { brimShape: ['other'] }, lineItemProperty: 'Brim Shape Detail', placeholder: 'e.g., asymmetric with front curl', maxLength: 100 }
          ]
        },
        {
          id: 'ribbon', order: 4, title: 'Ribbon', subtitle: 'Optional upgrade for your hat', required: false, type: 'multiField',
          fields: [
            { id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
              options: [ { value: 'none', label: 'No Change', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 }, { value: 'wide', label: 'Wide 1\u00BD"', price: 45 } ], defaultValue: 'none' },
            { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
            { id: 'secondRibbonColor', label: 'Second Ribbon Color', type: 'swatchPicker', required: false, showWhen: { ribbonType: ['narrow', 'wide'] }, lineItemProperty: 'Second Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' }
          ]
        },
        sharedNotesStep(5),
        sharedConfirmStep(6)
      ];

      // E6: Fedora — ribbon w/ double + bound edge, no head/crown/brim (BSS 271070)
      const fedoraSteps = [
        sharedCustomizeStep(1, 'Choose to customize your ribbon and edge'),
        {
          id: 'ribbon', order: 2, title: 'Ribbon & Edge', subtitle: 'Optional upgrades for your hat', required: false, type: 'multiField',
          fields: [
            { id: 'ribbonType', label: 'Custom Ribbon', type: 'buttonGroup', required: true, lineItemProperty: 'Ribbon Type',
              options: [ { value: 'none', label: 'No Change', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 }, { value: 'wide', label: 'Wide 1\u00BD"', price: 45 }, { value: 'double', label: 'Double Ribbons', price: 60 } ], defaultValue: 'none' },
            { id: 'ribbonColor', label: 'Ribbon Color', type: 'swatchPicker', required: true, showWhen: { ribbonType: ['narrow', 'wide', 'double'] }, lineItemProperty: 'Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
            { id: 'secondRibbonColor', label: 'Second Ribbon Color', type: 'swatchPicker', required: false, showWhen: { ribbonType: ['double'] }, lineItemProperty: 'Second Ribbon Color', optionsRef: 'sharedLibraries.ribbonColors' },
            { id: 'boundEdge', label: 'Custom Bound Edge', type: 'buttonGroup', required: true, lineItemProperty: 'Bound Edge',
              options: [ { value: 'none', label: 'No Change', price: 0 }, { value: 'narrow', label: 'Narrow 5/8"', price: 45 } ], defaultValue: 'none' },
            { id: 'boundEdgeColor', label: 'Bound Edge Color', type: 'swatchPicker', required: true, showWhen: { boundEdge: ['narrow'] }, lineItemProperty: 'Bound Edge Color', optionsRef: 'sharedLibraries.ribbonColors' }
          ]
        },
        sharedNotesStep(3),
        sharedConfirmStep(4)
      ];

      // E7: School of Mines — head shape + hat style dropdown + branding (BSS 275355)
      const schoolOfMinesSteps = [
        sharedCustomizeStep(1),
        sharedShapeMethodStep(2),
        sharedHeadShapeStep(3),
        {
          id: 'hatStyle', order: 3, title: 'Hat Style', subtitle: 'Select your preferred hat style', required: true, type: 'multiField',
          fields: [
            { id: 'hatStyle', label: 'Hat Style', type: 'dropdown', required: true, lineItemProperty: 'Hat Style',
              options: [
                { value: 'traditional', label: 'Traditional Shape' },
                { value: 'contemporary', label: 'Contemporary Shape' }
              ] }
          ]
        },
        {
          id: 'branding', order: 4, title: 'Personalization', subtitle: 'Add your branding to the hat', required: false, type: 'multiField',
          fields: [
            { id: 'wantBranding', label: 'Do you want branding?', type: 'buttonGroup', required: true, lineItemProperty: 'Branding',
              options: [ { value: 'no', label: 'No Branding' }, { value: 'yes', label: 'Yes, Add Branding' } ], defaultValue: 'no' },
            { id: 'brandingText', label: 'Branding Letters', type: 'textField', required: true, showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Text',
              placeholder: 'Enter up to 6 letters', maxLength: 6, helpText: 'First 3 letters: $20, Additional letters: $5 each',
              pricing: { type: 'tiered', tiers: [{ from: 1, to: 3, price: 20 }, { from: 4, to: 6, pricePerUnit: 5 }] } },
            { id: 'brandingPlacement', label: 'Branding Placement', type: 'dropdown', required: true, showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Placement',
              options: [ { value: 'back-left', label: 'Back Left Side' }, { value: 'back-middle', label: 'Back Middle' }, { value: 'back-right', label: 'Back Right Side' }, { value: 'other', label: 'Other (specify in notes)' } ] },
            { id: 'brandingConfirm', label: 'Confirm Branding', type: 'checkbox', required: true, showWhen: { wantBranding: ['yes'] }, lineItemProperty: 'Branding Confirmed',
              checkboxLabel: 'I understand that branding is permanent and makes the hat non-returnable' }
          ]
        },
        sharedConfirmStep(5)
      ];

      // Hat can color step — prepended to free-can option sets
      const hatCanColorStep = {
        id: 'hatCanColor',
        order: 0,
        title: 'Choose Your Free Hat Can',
        subtitle: 'Your hat includes a complimentary large western hat can — pick your color',
        required: true,
        type: 'swatchPicker',
        lineItemProperty: 'Free Hat Can Color',
        options: [
          { value: 'Black',  label: 'Black',  color: '#1a1a1a' },
          { value: 'Grey',   label: 'Grey',   color: '#9e9e9e' },
          { value: 'Maroon', label: 'Maroon', color: '#8b1a1a' },
          { value: 'Navy',   label: 'Navy',   color: '#1a2a5e' },
          { value: 'Purple', label: 'Purple', color: '#6b2f8c' },
          { value: 'Royal',  label: 'Royal',  color: '#1c4fa0' },
          { value: 'Teal',   label: 'Teal',   color: '#006d6d' }
        ]
      };

      // ---- All option sets ----
      const sets = {
        // Group A: Full option sets (same steps, different default brim)
        'rodeo-king-felt-4.5':          { id: 'rodeo-king-felt-4.5',          name: 'Rodeo King Felt 4\u00BD" Full Options',          steps: withDefaultBrim(fullOptionSteps, '4.5') },
        'rodeo-king-felt-4.5-free-can': { id: 'rodeo-king-felt-4.5-free-can', name: 'Rodeo King Felt 4\u00BD" + Free Hat Can',         steps: [hatCanColorStep, ...withDefaultBrim(fullOptionSteps, '4.5')] },
        'rodeo-king-felt-5':            { id: 'rodeo-king-felt-5',            name: 'Rodeo King Felt 5" Full Options',                steps: withDefaultBrim(fullOptionSteps, '5') },
        'bailey-felt-5':                { id: 'bailey-felt-5',                name: 'Bailey Felt 5" Full Options',                    steps: withDefaultBrim(fullOptionSteps, '5') },
        'felt-all-4.25':         { id: 'felt-all-4.25',         name: 'Felt All Options 4\u00BC"',             steps: withDefaultBrim(fullOptionSteps, '4.25') },
        'twister-felt-6x-5':     { id: 'twister-felt-6x-5',     name: 'Twister 6X 5" Full Options',           steps: withDefaultBrim(fullOptionSteps, '5') },
        'twister-felt-5':        { id: 'twister-felt-5',        name: 'Twister Felt 5" Full Options',          steps: withDefaultBrim(fullOptionSteps, '5') },
        'atwood-felt':           { id: 'atwood-felt',           name: 'Atwood Felt Full Options',              steps: withAtwoodHeadShape(withDefaultBrim(fullOptionSteps, '5')) },

        // Group B: Straw sets
        'straw':                 { id: 'straw',                 name: 'Straw Full Shaping',                    steps: strawSteps },
        'straw-atwood':          { id: 'straw-atwood',          name: 'Straw Atwood/Resistol/Cutbank',         steps: withAtwoodHeadShape(strawSteps) },
        'pro-hats':              { id: 'pro-hats',              name: 'Pro Hats',                              steps: strawSteps },

        // Group C: No Crown sets
        'felt-no-crown':         { id: 'felt-no-crown',         name: 'Felt No Crown (Resistol Wild Fire)',    steps: withDefaultBrim(feltNoCrownSteps, '4.5') },
        'straw-no-crown':        { id: 'straw-no-crown',        name: 'Straw No Crown',                       steps: strawNoCrownSteps },

        // Group D: Pre-shaped sets
        'pre-shaped-dallas':     { id: 'pre-shaped-dallas',     name: 'Pre Shaped Dallas',                    steps: preShapedRibbonSteps },
        'pre-shaped-bangora':    { id: 'pre-shaped-bangora',    name: 'Twister Pre-Shaped Bangora',           steps: preShapedRibbonSteps },
        'pre-shaped-shantung':   { id: 'pre-shaped-shantung',   name: 'Twister Shantung Pre Shaped',          steps: preShapedRibbonSteps },
        'pre-shaped-bailey':     { id: 'pre-shaped-bailey',     name: 'Bailey Pre-Shaped Wool',               steps: withDefaultBrim(preShapedBrimSteps, '4.5') },

        // Group E: Specialty / one-off sets
        'twister-3x-wool':      { id: 'twister-3x-wool',      name: 'Twister 3X Pre-Shaped Wool',            steps: withDefaultBrim(twister3xWoolSteps, '4.5') },
        'stetson':               { id: 'stetson',               name: 'Stetson',                               steps: withDefaultBrim(stetsonSteps, '4.5') },
        'serratelli-wool':       { id: 'serratelli-wool',       name: 'Serratelli Wool',                       steps: withDefaultBrim(serratelliSteps, '4.5') },
        'bailey-easton':         { id: 'bailey-easton',         name: 'Bailey Easton Pre Shaped',              steps: withDefaultBrim(baileyEastonSteps, '4.5') },
        'straw-limited':         { id: 'straw-limited',         name: 'Straw Pre-Creased Limited Shaping',     steps: strawLimitedSteps },
        'fedora':                { id: 'fedora',                name: 'Fedora',                                steps: fedoraSteps },
        'school-of-mines':       { id: 'school-of-mines',       name: 'School of Mines',                       steps: schoolOfMinesSteps }
      };

      return sets;
    }

    // =========================================================================
    // Stepper
    // =========================================================================

    buildStepper() {
      // Build a stepper that reflects current step visibility. Hidden steps
      // are skipped entirely. The displayed step number is based on
      // visible-step position so customers see a clean sequence.
      let visibleNumber = 0;
      const stepperHtml = this.steps.map((step, index) => {
        if (!this._isStepVisible(step)) return '';
        visibleNumber += 1;
        return `
        <li class="hat-builder-stepper__step" data-step-index="${index}">
          <button type="button"
                  class="hat-builder-stepper__button"
                  role="tab"
                  id="step-tab-${index}"
                  aria-selected="${index === this.currentStep ? 'true' : 'false'}"
                  aria-controls="step-panel-${index}"
                  ${index > 0 ? 'disabled' : ''}>
            <span class="hat-builder-stepper__number"><span>${visibleNumber}</span></span>
            <span class="hat-builder-stepper__label">${step.title}</span>
          </button>
        </li>
      `;
      }).filter(Boolean).join('');

      this.els.stepper.innerHTML = stepperHtml;
    }

    updateStepperState() {
      const stepItems = this.els.stepper.querySelectorAll('.hat-builder-stepper__step');

      stepItems.forEach((item, index) => {
        const btn = item.querySelector('.hat-builder-stepper__button');

        if (index < this.currentStep) {
          item.classList.add('is-completed');
          btn.disabled = false;
        } else {
          item.classList.remove('is-completed');
        }

        btn.setAttribute('aria-selected', index === this.currentStep ? 'true' : 'false');

        if (index > this.currentStep) {
          // In prefill mode, enable all steps so user can review any pre-filled step
          btn.disabled = !this._prefillMode;
        }

        // Mark pre-filled steps as completed in the stepper
        if (this._prefillMode && index < this.currentStep) {
          item.classList.add('is-completed');
        }
      });

      // Update mobile compact stepper
      const mobileContainer = this.container.querySelector('.hat-builder-stepper__mobile');
      if (mobileContainer) {
        const currentEl = mobileContainer.querySelector('[data-current-step]');
        const totalEl = mobileContainer.querySelector('[data-total-steps]');
        const titleEl = mobileContainer.querySelector('[data-step-title]');
        if (currentEl) currentEl.textContent = this.currentStep + 1;
        if (totalEl) totalEl.textContent = this.steps.length;
        if (titleEl) titleEl.textContent = this.steps[this.currentStep]?.title || '';
      }
    }

    updateProgress() {
      const progress = ((this.currentStep + 1) / this.steps.length) * 100;
      this.els.progressBar.style.width = progress + '%';
    }

    // =========================================================================
    // Step Rendering
    // =========================================================================

    renderStep(stepIndex) {
      const step = this.steps[stepIndex];
      if (!step) return;

      const isInitialRender = !this._initialRenderDone;
      this._initialRenderDone = true;

      this.currentStep = stepIndex;

      // Build step content
      const prefillBanner = this._prefillMode ? `
            <div class="hb-prefill-banner" role="status">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>Standard setup loaded &mdash; review or adjust each step</span>
              <button type="button" class="hb-prefill-banner__close" aria-label="Dismiss banner">&times;</button>
            </div>` : '';

      const multiFieldProgress = this._renderMultiFieldProgress(step);

      const galleryLink = (step.id === 'crownShape' || step.id === 'brimStyle') ?
        `<a href="/pages/hat-shape-gallery" class="hb-gallery-link" target="_blank">View ${step.id === 'crownShape' ? 'Crown' : 'Brim'} Gallery</a>` : '';

      const contentHtml = `
        <div class="hat-builder__step" id="step-panel-${stepIndex}" role="tabpanel" aria-labelledby="step-tab-${stepIndex}">
          <header class="hat-builder__step-header">
            <h3 class="hat-builder__step-title" tabindex="-1">${step.title}</h3>
            ${step.subtitle ? `<p class="hat-builder__step-subtitle">${step.subtitle}</p>` : ''}
            ${galleryLink}
            ${prefillBanner}
            ${multiFieldProgress}
            ${step.helpText ? `<p class="hat-builder__step-help">${step.helpText}</p>` : ''}
            ${step.helpGuide ? this._renderHelpGuide(step.helpGuide) : ''}
          </header>
          <div class="hat-builder__step-content" data-step-id="${step.id}">
            ${step.id === 'confirmation' ? this._renderSizeVerification() : ''}
            ${this.renderStepOptions(step)}
          </div>
        </div>
      `;

      this.els.content.innerHTML = contentHtml;

      // Critical path: apply selections + bind events first (must be synchronous)
      this.applySelections(step);
      this.bindStepEvents();
      this.updateConditionalFields();

      // Defer non-critical UI updates so the browser can paint the step content immediately
      requestAnimationFrame(() => {
        // Bind review "Change" buttons on the confirmation step
        if (step.id === 'confirmation') {
          this.els.content.querySelectorAll('.hb-size-verify__edit[data-goto-step]').forEach(btn => {
            btn.addEventListener('click', () => {
              const idx = parseInt(btn.dataset.gotoStep, 10);
              if (!isNaN(idx)) this.renderStep(idx);
            });
          });
        }

        this._renderSelectionsSummary();
        this.updateStepperState();
        this.updateNavigation();
        this.updateProgress();
        this.calculatePrice();

        // Focus heading for screen readers, but suppress the browser's default
        // focus-scroll — on mobile it lands the heading at the very top of the
        // viewport (often under the sticky header) and leaves the customer
        // hunting for the next button.
        const heading = this.els.content.querySelector('.hat-builder__step-title');
        if (heading) heading.focus({ preventScroll: true });

        // After a user-triggered navigation, scroll the new step into view at
        // the top of the viewport, accounting for the sticky page header.
        // Skipped on the very first render so the product image stays in view
        // when the page loads.
        if (!isInitialRender && this.els.content) {
          const headerOffset = this._getStickyHeaderHeight() + 12;
          const targetY = this.els.content.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }
      });
    }

    _renderHelpGuide(guide) {
      const itemsHtml = guide.items.map(item => `
        <div class="hb-help-guide__item">
          <dt class="hb-help-guide__label">${item.label}</dt>
          <dd class="hb-help-guide__text">${item.text}</dd>
        </div>
      `).join('');

      return `
        <details class="hb-help-guide">
          <summary class="hb-help-guide__trigger">${guide.trigger}</summary>
          <div class="hb-help-guide__content">
            ${guide.intro ? `<p class="hb-help-guide__intro">${guide.intro}</p>` : ''}
            <dl class="hb-help-guide__list">${itemsHtml}</dl>
            ${guide.tip ? `<p class="hb-help-guide__tip">${guide.tip}</p>` : ''}
          </div>
        </details>
      `;
    }

    renderStepOptions(step) {
      switch (step.type) {
        case 'buttonGroup':
          return this.renderButtonGroup(step);
        case 'thumbnailPicker':
          return this.renderThumbnailPicker(step);
        case 'swatchPicker':
          return this.renderSwatchPicker(step);
        case 'multiField':
          return this.renderMultiField(step);
        case 'textField':
          return this.renderTextField(step);
        case 'textArea':
          return this.renderTextArea(step);
        case 'dropdown':
          return this.renderDropdown(step);
        case 'checkbox':
          return this.renderCheckbox(step);
        case 'radioGroup':
          return this.renderRadioGroup(step);
        case 'fileUpload':
          return this.renderFileUpload(step);
        default:
          return '<p>Unknown option type: ' + step.type + '</p>';
      }
    }

    // =========================================================================
    // Field Type Renderers
    // =========================================================================

    renderButtonGroup(step) {
      const options = step.options || this.getLibraryOptions(step.optionsRef) || [];
      const hasSvg = options.some(opt => opt.svg);

      const optionsHtml = options.map(opt => {
        const isDisabled = opt.disabled === true;
        const disabledStyle = isDisabled
          ? 'style="opacity:0.45;cursor:not-allowed;text-decoration:line-through;text-decoration-color:rgba(0,0,0,0.35);"'
          : '';
        return `
        <button type="button"
                class="hb-button-opt ${hasSvg ? 'hb-button-opt--has-icon' : ''} ${isDisabled ? 'hb-button-opt--disabled' : ''}"
                role="radio"
                aria-checked="false"
                aria-disabled="${isDisabled ? 'true' : 'false'}"
                ${isDisabled ? 'disabled' : ''}
                data-value="${opt.value}"
                data-action="${opt.action || ''}"
                ${disabledStyle}>
          ${opt.svg ? `<span class="hb-button-opt__icon" aria-hidden="true">${opt.svg}</span>` : ''}
          <span class="hb-button-opt__label">${opt.label}</span>
          ${opt.description ? `<span class="hb-button-opt__desc">${opt.description}</span>` : ''}
          ${opt.price ? `<span class="hb-button-opt__price">+${this.formatMoney(opt.price * 100)}</span>` : ''}
        </button>
      `;
      }).join('');

      return `
        <div class="hb-buttons" data-component="buttons" data-field-id="${step.id}" data-size="${options.length <= 2 ? 'large' : ''}" role="radiogroup" aria-label="${step.title || step.label || ''}">
          <div class="hb-buttons__group">
            ${optionsHtml}
          </div>
        </div>
      `;
    }

    renderThumbnailPicker(step) {
      let html = '';

      if (step.categories) {
        // Render category tabs + tabpanels
        const tabsHtml = step.categories.map((cat, i) => `
          <button type="button" class="hb-thumbnails__category-tab"
                  role="tab" aria-selected="${i === 0 ? 'true' : 'false'}"
                  data-category="${cat.id}"
                  aria-controls="hb-cat-panel-${step.id}-${cat.id}"
                  id="hb-cat-tab-${step.id}-${cat.id}">
            ${cat.name}
          </button>
        `).join('');

        const panelsHtml = step.categories.map((cat, i) => {
          const options = this.getLibraryOptions(cat.optionsRef) || [];
          return `
            <div id="hb-cat-panel-${step.id}-${cat.id}" role="tabpanel"
                 aria-labelledby="hb-cat-tab-${step.id}-${cat.id}"
                 ${i > 0 ? 'hidden' : ''}>
              <div class="hb-thumbnails__grid">
                ${this._renderThumbnailOptions(options)}
              </div>
            </div>
          `;
        }).join('');

        html = `
          <div class="hb-thumbnails" data-component="thumbnails" data-field-id="${step.id}" role="radiogroup" aria-label="${step.title || step.label || ''}">
            <div class="hb-thumbnails__categories" role="tablist">
              ${tabsHtml}
            </div>
            ${panelsHtml}
          </div>
        `;
      } else {
        // Simple grid without categories
        const options = step.options || this.getLibraryOptions(step.optionsRef) || [];
        html = `
          <div class="hb-thumbnails" data-component="thumbnails" data-field-id="${step.id}" role="radiogroup" aria-label="${step.title || step.label || ''}">
            <div class="hb-thumbnails__grid">
              ${this._renderThumbnailOptions(options)}
            </div>
          </div>
        `;
      }

      // Append conditional fields (e.g., brick initial letter)
      if (step.conditionalFields) {
        step.conditionalFields.forEach(cf => {
          const isVisible = cf.showWhen && cf.showWhen.optionValue &&
            this.selections[step.id] && cf.showWhen.optionValue.includes(this.selections[step.id]);
          const field = cf.field;
          html += `
            <div class="hat-builder__field hat-builder__conditional-field"
                 data-field-wrapper="${field.id}"
                 data-show-when-parent="${step.id}"
                 data-show-when-values='${JSON.stringify(cf.showWhen.optionValue)}'
                 ${isVisible ? '' : 'hidden'}>
              <label class="hat-builder__field-label">${field.label}</label>
              ${this.renderStepOptions({ ...field, title: field.label })}
            </div>
          `;
        });
      }

      return html;
    }

    _renderThumbnailOptions(options) {
      return options.map(opt => {
        let imageContent;
        if (opt.svg) {
          // Inline SVG illustration
          imageContent = `<span class="hb-thumbnail__svg" aria-hidden="true">${opt.svg}</span>`;
        } else if (opt.image) {
          // Image from Shopify Files with fallback to text
          imageContent = `<img src="${this.getImageUrl(opt.image)}" alt="${opt.alt || opt.label}" class="hb-thumbnail__image" loading="lazy" onerror="${this._buildImageFallback()}"><span class="hb-thumbnail__placeholder-text" style="display:none">${opt.label}</span>`;
        } else {
          // Text-only placeholder
          imageContent = `<span class="hb-thumbnail__placeholder-text">${opt.label}</span>`;
        }

        return `
          <button type="button"
                  class="hb-thumbnail"
                  role="radio"
                  aria-checked="false"
                  data-value="${opt.value}"
                  ${opt.desc ? `title="${opt.desc}"` : ''}>
            <span class="hb-thumbnail__image-wrap">
              ${imageContent}
            </span>
            <span class="hb-thumbnail__label">${opt.label}</span>
            <span class="hb-thumbnail__check" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
          </button>
        `;
      }).join('');
    }

    // =========================================================================
    // URL Param + Same-Page Pre-selection
    // =========================================================================

    /**
     * Check if we're editing an existing cart item
     */
    _checkEditMode() {
      try {
        const params = new URLSearchParams(window.location.search);
        const editMode = params.get('edit');
        const lineKey = params.get('key');
        
        if (editMode === 'true' && lineKey) {
          this._editingLineKey = lineKey;
          console.log('[HatBuilder] Edit mode detected, line key:', lineKey);
        }
      } catch (e) {
        console.error('[HatBuilder] Error checking edit mode:', e);
      }
    }

    /**
     * Build a URL that reconstructs the current builder state for cart editing.
     */
    _buildEditUrl(lineKey = null) {
      const fieldToParam = {
        'crownShape': 'crown',
        'headShape': 'head',
        'brimSize': 'brimSize',
        'brimShape': 'brimShape',
        'brimFront': 'brimFront',
        'ribbonType': 'ribbon',
        'ribbonColor': 'ribbonColor',
        'boundEdge': 'edge',
        'boundEdgeColor': 'edgeColor',
        'wantBranding': 'brand'
      };

      const params = new URLSearchParams();
      
      // CRITICAL: Include variant ID so edit-cart returns to correct size
      if (this.config.variantId) {
        params.set('variant', this.config.variantId);
      }
      
      for (const [fieldId, paramName] of Object.entries(fieldToParam)) {
        if (this.selections[fieldId]) {
          params.set(paramName, this.selections[fieldId]);
        }
      }
      if (this._gallerySource) {
        params.set('source', this._gallerySource);
      }
      
      // Add edit mode params if this is a cart edit URL
      if (lineKey) {
        params.set('edit', 'true');
        params.set('key', lineKey);
      }

      return window.location.pathname + '?' + params.toString();
    }

    /**
     * Read URL params and pre-fill builder selections.
     * Supports: ?crown=&head=&brimSize=&brimShape=&brimFront=&ribbon=&edge=&brand=
     */
    _readUrlParams() {
      try {
        const params = new URLSearchParams(window.location.search);
        const crownParam = params.get('crown');
        if (!crownParam) return;

        // Validate crown exists in options
        const crownStep = this.steps.find(s => s.id === 'crownShape');
        if (!crownStep) return;
        const allCrowns = this._collectAllOptions(crownStep);
        if (!allCrowns.find(opt => opt.value === crownParam)) {
          console.warn('[HatBuilder] Invalid crown in URL:', crownParam);
          return;
        }

        // Map URL param names to builder field IDs
        const paramMap = {
          'crown': 'crownShape',
          'head': 'headShape',
          'brimSize': 'brimSize',
          'brimShape': 'brimShape',
          'brimFront': 'brimFront',
          'ribbon': 'ribbonType',
          'ribbonColor': 'ribbonColor',
          'edge': 'boundEdge',
          'edgeColor': 'boundEdgeColor',
          'brand': 'wantBranding'
        };

        // Always set customize = yes when coming from gallery
        this.selections['customize'] = 'yes';

        // Read and validate each param
        for (const [urlKey, fieldId] of Object.entries(paramMap)) {
          const value = params.get(urlKey);
          if (!value) continue;
          if (this._isValidFieldValue(fieldId, value)) {
            this.selections[fieldId] = value;
          }
        }

        // Track gallery source for order attribution
        const source = params.get('source');
        if (source) this._gallerySource = source;

        // Pre-fill notes with gallery description for reference
        const desc = params.get('desc');
        if (desc) {
          this.selections['notes'] = `Gallery style: ${source || 'Custom'} — ${desc}`;
        }

        this._prefillMode = true;
        console.log('[HatBuilder] Pre-filled from URL:', Object.keys(this.selections).length, 'fields');
      } catch (e) {
        console.warn('[HatBuilder] Could not read URL params:', e.message);
      }
    }

    /**
     * Listen for same-page crown preselection events from the gallery.
     * Accepts all default fields from the gallery section.
     */
    _bindCrownPreselectEvent() {
      window.addEventListener('hat-builder:preselect-crown', (e) => {
        const detail = e.detail || {};
        const crownValue = detail.crown;
        if (!crownValue) return;

        // Validate crown
        const crownStep = this.steps.find(s => s.id === 'crownShape');
        if (!crownStep) return;
        const allCrowns = this._collectAllOptions(crownStep);
        const validOption = allCrowns.find(opt => opt.value === crownValue);
        if (!validOption) {
          console.warn('[HatBuilder] Invalid crown from event:', crownValue);
          return;
        }

        // Map event detail keys to builder field IDs
        const detailMap = {
          'crown': 'crownShape',
          'head': 'headShape',
          'brimSize': 'brimSize',
          'brimShape': 'brimShape',
          'brimFront': 'brimFront',
          'ribbon': 'ribbonType',
          'ribbonColor': 'ribbonColor',
          'edge': 'boundEdge',
          'edgeColor': 'boundEdgeColor',
          'brand': 'wantBranding'
        };

        this.selections['customize'] = 'yes';

        for (const [key, fieldId] of Object.entries(detailMap)) {
          const value = detail[key];
          if (value && this._isValidFieldValue(fieldId, value)) {
            this.selections[fieldId] = value;
          }
        }

        // Track gallery source for order attribution
        if (detail.source) this._gallerySource = detail.source;

        // Pre-fill notes with gallery description for reference
        if (detail.description) {
          this.selections['notes'] = `Gallery style: ${detail.source || 'Custom'} — ${detail.description}`;
        }

        this._prefillMode = true;
        console.log('[HatBuilder] Pre-filled from gallery:', Object.keys(this.selections).length, 'fields');

        // Show welcome/verification screen
        this._renderWelcomeScreen();
      });
    }

    /**
     * Show a toast notification confirming pre-fill selections.
     * Appears at the top of the builder, auto-dismisses after 6 seconds.
     */
    _showPrefillToast(crownLabel) {
      // Remove any existing toast
      const existing = this.container.querySelector('.hb-toast');
      if (existing) existing.remove();

      const toast = document.createElement('div');
      toast.className = 'hb-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = `
        <div class="hb-toast__content">
          <svg class="hb-toast__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <span><strong>${crownLabel}</strong> loaded with standard setup. Review each step below.</span>
          <button type="button" class="hb-toast__close" aria-label="Dismiss">&times;</button>
        </div>
      `;

      // Insert at the very top of the builder main area
      this.els.main.insertBefore(toast, this.els.main.firstChild);

      // Trigger entrance animation
      requestAnimationFrame(() => {
        toast.classList.add('hb-toast--visible');
      });

      // Close button
      toast.querySelector('.hb-toast__close').addEventListener('click', () => {
        toast.classList.remove('hb-toast--visible');
        setTimeout(() => toast.remove(), 300);
      });

      // Auto-dismiss after 6 seconds
      setTimeout(() => {
        if (toast.parentNode) {
          toast.classList.remove('hb-toast--visible');
          setTimeout(() => toast.remove(), 300);
        }
      }, 6000);
    }

    /**
     * Render a welcome/verification screen when arriving from the gallery.
     * Shows all pre-filled selections and highlights optional extras.
     */
    _renderWelcomeScreen() {
      // Build pre-filled selections list
      let selectionsHtml = '';
      const skipIds = ['customize', 'sizeConfirmation', 'requestFitGuide', 'brandingConfirm'];

      this.steps.forEach(step => {
        if (step.type === 'multiField' && step.fields) {
          step.fields.forEach(field => {
            if (skipIds.includes(field.id)) return;
            const val = this.selections[field.id];
            if (val && val !== '' && val !== 'none' && val !== 'no') {
              const label = this._getFieldSelectionLabel(field, val);
              selectionsHtml += `
                <div class="hb-welcome__item">
                  <span class="hb-welcome__item-label">${field.label}</span>
                  <span class="hb-welcome__item-value">${label}</span>
                </div>`;
            }
          });
        } else if (step.lineItemProperty && !skipIds.includes(step.id)) {
          const val = this.selections[step.id];
          if (val && val !== '' && val !== 'none' && val !== 'no') {
            const label = this._getFieldSelectionLabel(step, val);
            selectionsHtml += `
              <div class="hb-welcome__item">
                <span class="hb-welcome__item-label">${step.title}</span>
                <span class="hb-welcome__item-value">${label}</span>
              </div>`;
          }
        }
      });

      // Build optional extras list (steps that are not required and have no meaningful selection)
      let extrasHtml = '';
      const extraDescriptions = {
        'ribbon': { label: 'Ribbon & Trim', desc: 'Add a custom ribbon in your choice of color' },
        'branding': { label: 'Personalization', desc: 'Add your initials or custom text' },
        'notesAndPhotos': { label: 'Notes & Photos', desc: 'Share reference photos or special instructions' }
      };

      this.steps.forEach((step, stepIndex) => {
        if (step.required !== false) return;
        const extra = extraDescriptions[step.id];
        if (!extra) return;
        extrasHtml += `
          <button type="button" class="hb-welcome__extra-item" data-goto-step="${stepIndex}">
            <svg class="hb-welcome__extra-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <div class="hb-welcome__extra-text">
              <strong>${extra.label}</strong>
              <span class="hb-welcome__extra-desc">${extra.desc}</span>
            </div>
            <svg class="hb-welcome__extra-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>`;
      });

      // Source name for header
      const sourceName = this._gallerySource || '';
      const sourceText = sourceName
        ? `Based on the <strong>${sourceName}</strong> style, here's what we've set up for you:`
        : `Here's what we've set up for you:`;

      const contentHtml = `
        <div class="hb-welcome" role="region" aria-label="Your hat starting point">
          <div class="hb-welcome__header">
            <svg class="hb-welcome__check" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <h3 class="hb-welcome__title">Your Starting Point</h3>
          </div>

          <p class="hb-welcome__intro">${sourceText}</p>

          <div class="hb-welcome__selections">
            ${selectionsHtml}
          </div>

          ${extrasHtml ? `
          <div class="hb-welcome__extras">
            <p class="hb-welcome__extras-title">You can also add:</p>
            ${extrasHtml}
          </div>` : ''}

          <p class="hb-welcome__note">Every choice can be changed as you go through each step.</p>

          <div class="hb-welcome__actions">
            <button type="button" class="hb-welcome__btn hb-welcome__btn--review">
              Review Step by Step
            </button>
            <button type="button" class="hb-welcome__btn hb-welcome__btn--continue">
              Looks Good, Continue
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      `;

      this.els.content.innerHTML = contentHtml;

      // Update summary sidebar
      this._renderSelectionsSummary();

      // Bind welcome screen button handlers
      const reviewBtn = this.els.content.querySelector('.hb-welcome__btn--review');
      const continueBtn = this.els.content.querySelector('.hb-welcome__btn--continue');

      if (reviewBtn) {
        reviewBtn.addEventListener('click', () => {
          this.renderStep(0);
        });
      }

      if (continueBtn) {
        continueBtn.addEventListener('click', () => {
          const idx = this._findFirstIncompleteStep();
          this.renderStep(idx);
        });
      }

      // Bind extra item click handlers (jump directly to that step)
      this.els.content.querySelectorAll('.hb-welcome__extra-item[data-goto-step]').forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.gotoStep, 10);
          if (!isNaN(idx)) this.renderStep(idx);
        });
      });

      // Announce to screen readers
      this.announce('Your hat starting point is ready. Review your pre-selected options or continue to customize.');
    }

    /**
     * Get the height of any sticky header element on the page.
     */
    _getStickyHeaderHeight() {
      const header = document.querySelector('sticky-header, .shopify-section-header-sticky, header.header');
      if (!header) return 0;
      const style = window.getComputedStyle(header);
      if (style.position === 'sticky' || style.position === 'fixed') {
        return header.getBoundingClientRect().height;
      }
      return 0;
    }

    /**
     * Scroll to the next incomplete sub-field in a multiField step.
     * Called after a user makes a selection to guide them to the next field.
     */
    _scrollToNextIncompleteSubField(justCompletedFieldId) {
      const step = this.steps[this.currentStep];
      if (!step || step.type !== 'multiField' || !step.fields) return;

      // Get visible fields in order
      const visibleFields = step.fields.filter(field => {
        if (field.showWhen) {
          for (const [fId, allowed] of Object.entries(field.showWhen)) {
            const current = this.selections[fId];
            if (!current || !allowed.includes(current)) return false;
          }
        }
        return true;
      });

      // Find the index of the just-completed field
      const justIdx = visibleFields.findIndex(f => f.id === justCompletedFieldId);

      // Look for the next incomplete required field after the just-completed one
      let nextField = null;
      for (let i = justIdx + 1; i < visibleFields.length; i++) {
        if (visibleFields[i].required && !this.selections[visibleFields[i].id]) {
          nextField = visibleFields[i];
          break;
        }
      }

      if (nextField) {
        // Target the field wrapper (includes label) not the inner component
        const fieldEl = this.els.content.querySelector(`[data-field-wrapper="${nextField.id}"]`)
          || this.els.content.querySelector(`[data-field-id="${nextField.id}"]`);
        if (!fieldEl) return;

        const headerOffset = this._getStickyHeaderHeight() + 24;
        const rect = fieldEl.getBoundingClientRect();
        // Only scroll if the field label isn't already visible in the top half
        const idealZone = window.innerHeight * 0.5;
        const needsScroll = rect.top < headerOffset || rect.top > idealZone;

        if (needsScroll) {
          const targetY = rect.top + window.pageYOffset - headerOffset;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }

        // Highlight the inner component for visual feedback
        const innerEl = this.els.content.querySelector(`[data-field-id="${nextField.id}"]`) || fieldEl;
        innerEl.classList.add('hb-field--highlight');
        setTimeout(() => innerEl.classList.remove('hb-field--highlight'), 2000);
      } else {
        // All sub-fields complete — highlight the Next button
        const nextBtn = this.els.nextBtn;
        if (nextBtn && !nextBtn.hidden) {
          nextBtn.classList.add('hb-btn--highlight');
          setTimeout(() => nextBtn.classList.remove('hb-btn--highlight'), 1800);

          // Scroll Next button into view if needed
          const rect = nextBtn.getBoundingClientRect();
          if (rect.bottom > window.innerHeight) {
            nextBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }

    /**
     * Render a progress indicator for multiField steps (mobile-friendly).
     * Returns HTML string. Shows "X of Y fields complete".
     */
    _renderMultiFieldProgress(step) {
      if (!step || step.type !== 'multiField' || !step.fields) return '';

      const visibleFields = step.fields.filter(field => {
        if (field.showWhen) {
          for (const [fId, allowed] of Object.entries(field.showWhen)) {
            const current = this.selections[fId];
            if (!current || !allowed.includes(current)) return false;
          }
        }
        return field.required;
      });

      if (visibleFields.length <= 1) return '';

      const completed = visibleFields.filter(f => this.selections[f.id]).length;
      const pct = Math.round((completed / visibleFields.length) * 100);

      return `
        <div class="hb-multifield-progress" data-multifield-progress>
          <span class="hb-multifield-progress__text">${completed} of ${visibleFields.length} fields complete</span>
          <div class="hb-multifield-progress__bar">
            <div class="hb-multifield-progress__fill" style="width: ${pct}%"></div>
          </div>
        </div>
      `;
    }

    /**
     * Update the multiField progress indicator after a selection.
     */
    _updateMultiFieldProgress() {
      const step = this.steps[this.currentStep];
      const el = this.els.content.querySelector('[data-multifield-progress]');
      if (!el || !step) return;

      const visibleFields = step.fields.filter(field => {
        if (field.showWhen) {
          for (const [fId, allowed] of Object.entries(field.showWhen)) {
            const current = this.selections[fId];
            if (!current || !allowed.includes(current)) return false;
          }
        }
        return field.required;
      });

      if (visibleFields.length <= 1) return;

      const completed = visibleFields.filter(f => this.selections[f.id]).length;
      const pct = Math.round((completed / visibleFields.length) * 100);

      const text = el.querySelector('.hb-multifield-progress__text');
      const fill = el.querySelector('.hb-multifield-progress__fill');
      if (text) text.textContent = `${completed} of ${visibleFields.length} fields complete`;
      if (fill) fill.style.width = `${pct}%`;
    }

    /**
     * Validate a value against a field's available options.
     * Searches all steps and multiField sub-fields for the given fieldId.
     */
    _isValidFieldValue(fieldId, value) {
      for (const step of this.steps) {
        // Check top-level step
        if (step.id === fieldId) {
          const options = step.options || this.getLibraryOptions(step.optionsRef) || this._collectAllOptions(step);
          return options.some(opt => opt.value === value);
        }
        // Check multiField sub-fields
        if (step.type === 'multiField' && step.fields) {
          for (const field of step.fields) {
            if (field.id === fieldId) {
              const options = field.options || this.getLibraryOptions(field.optionsRef) || [];
              return options.some(opt => opt.value === value);
            }
          }
        }
      }
      return false;
    }

    /**
     * Find the first step that has incomplete required selections.
     * Used when pre-filling to skip past already-configured steps.
     * Returns the step index (defaults to last step if all complete).
     */
    _findFirstIncompleteStep() {
      for (let i = 0; i < this.steps.length; i++) {
        const step = this.steps[i];

        // Skip hidden steps entirely — they don't count as incomplete.
        if (!this._isStepVisible(step)) continue;

        if (step.type === 'multiField' && step.fields) {
          // Check each required visible sub-field
          for (const field of step.fields) {
            // Skip conditional fields whose conditions aren't met
            if (field.showWhen && !this._prefillConditionMet(field.showWhen)) continue;
            if (field.required && !this.selections[field.id]) {
              return i;
            }
          }
        } else if (step.required) {
          if (!this.selections[step.id]) {
            return i;
          }
        }
      }
      // All complete — go to last step (confirmation)
      return this.steps.length - 1;
    }

    /**
     * Simple condition check for pre-fill step scanning.
     * Checks if the showWhen conditions are met based on current selections.
     */
    _prefillConditionMet(showWhen) {
      for (const [fieldId, allowedValues] of Object.entries(showWhen)) {
        const current = this.selections[fieldId];
        if (!current || !allowedValues.includes(current)) return false;
      }
      return true;
    }

    _collectAllOptions(step) {
      let allOptions = [];
      if (step.categories) {
        step.categories.forEach(cat => {
          const opts = this.getLibraryOptions(cat.optionsRef) || [];
          allOptions = allOptions.concat(opts);
        });
      } else {
        allOptions = step.options || this.getLibraryOptions(step.optionsRef) || [];
      }
      return allOptions;
    }


    renderSwatchPicker(step) {
      const options = step.options || this.getLibraryOptions(step.optionsRef) || [];

      const optionsHtml = options.map(opt => {
        const isLight = this.isLightColor(opt.color);
        return `
          <button type="button"
                  class="hb-swatch"
                  role="radio"
                  aria-checked="false"
                  aria-label="${opt.label}"
                  data-value="${opt.value}"
                  data-color="${opt.color || ''}"
                  ${isLight ? 'data-light="true"' : ''}>
            <span class="hb-swatch__color" style="background-color: ${opt.color || '#ccc'}; ${opt.image ? `background-image: url(${this.getImageUrl(opt.image)})` : ''}"></span>
            <span class="hb-swatch__check" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
          </button>
        `;
      }).join('');

      return `
        <div class="hb-swatches" data-component="swatches" data-field-id="${step.id}" role="radiogroup" aria-label="${step.title || step.label || ''}">
          <div class="hb-swatches__grid">
            ${optionsHtml}
          </div>
          <div class="hb-swatches__selected" aria-live="polite">
            <span class="hb-swatches__selected-label">Selected: </span>
            <span class="hb-swatches__selected-value">Select a color to continue</span>
          </div>
        </div>
      `;
    }

    renderMultiField(step) {
      if (!step.fields) return '';

      return step.fields.map(field => {
        const hasCondition = !!field.showWhen;
        const isHidden = hasCondition && !this.checkCondition(field.showWhen);
        const showWhenAttr = hasCondition
          ? `data-show-when='${JSON.stringify(field.showWhen)}'`
          : '';

        const fieldStep = { ...field, title: field.label };
        // Field types that render their own label internally
        const selfLabeledTypes = ['dropdown', 'textField', 'textArea', 'checkbox', 'radioGroup', 'fileUpload'];
        const needsOuterLabel = !selfLabeledTypes.includes(field.type);
        return `
          <div class="hat-builder__field"
               data-field-wrapper="${field.id}"
               ${showWhenAttr}
               ${isHidden ? 'hidden' : ''}>
            ${needsOuterLabel ? `<label class="hat-builder__field-label">${field.label}</label>` : ''}
            ${this.renderStepOptions(fieldStep)}
          </div>
        `;
      }).join('');
    }

    renderTextField(field) {
      const id = `hb-text-${field.id}`;
      return `
        <div class="hb-text-field" data-component="textField" data-field-id="${field.id}">
          <label for="${id}" class="hb-text-field__label">${field.label}</label>
          <input type="text"
                 id="${id}"
                 class="hb-text-field__input"
                 placeholder="${field.placeholder || ''}"
                 ${field.maxLength ? `maxlength="${field.maxLength}"` : ''}
                 ${field.required ? 'required aria-required="true"' : ''}
                 ${field.helpText ? `aria-describedby="${id}-help"` : ''}>
          ${field.helpText ? `<p id="${id}-help" class="hb-text-field__help">${field.helpText}</p>` : ''}
          ${field.maxLength ? `<span class="hb-text-field__count"><span data-count>0</span>/${field.maxLength}</span>` : ''}
        </div>
      `;
    }

    renderTextArea(field) {
      const id = `hb-textarea-${field.id}`;
      const maxLen = field.maxLength || 500;
      return `
        <div class="hb-textarea" data-component="textArea" data-field-id="${field.id}">
          <label for="${id}" class="hb-textarea__label">${field.label || 'Enter text'}</label>
          <textarea
            id="${id}"
            class="hb-textarea__input"
            rows="4"
            maxlength="${maxLen}"
            placeholder="${field.placeholder || ''}"
            ${field.required ? 'required aria-required="true"' : ''}
            ${field.helpText ? `aria-describedby="${id}-help"` : ''}></textarea>
          <div class="hb-textarea__footer">
            ${field.helpText ? `<span class="hb-textarea__help" id="${id}-help">${field.helpText}</span>` : '<span class="hb-textarea__help"></span>'}
            <span class="hb-textarea__count"><span data-count>0</span>/${maxLen}</span>
          </div>
        </div>
      `;
    }

    renderDropdown(field) {
      const options = field.options || this.getLibraryOptions(field.optionsRef) || [];
      const id = `hb-dropdown-${field.id}`;

      const optionsHtml = options.map(opt =>
        `<option value="${opt.value}" ${opt.default ? 'selected' : ''}>${opt.label}</option>`
      ).join('');

      return `
        <div class="hb-dropdown" data-component="dropdown" data-field-id="${field.id}">
          <label for="${id}" class="hb-dropdown__label">${field.label}</label>
          <div class="hb-dropdown__wrapper">
            <select id="${id}" class="hb-dropdown__select"
                    ${field.required ? 'required aria-required="true"' : ''}>
              <option value="">Select...</option>
              ${optionsHtml}
            </select>
          </div>
        </div>
      `;
    }

    renderCheckbox(field) {
      const id = `hb-check-${field.id}`;
      return `
        <div class="hb-checkbox" data-component="checkbox" data-field-id="${field.id}">
          <label class="hb-checkbox__label" for="${id}">
            <input type="checkbox" id="${id}" class="hb-checkbox__input"
                   ${field.required ? 'required aria-required="true"' : ''}
                   ${field.helpText ? `aria-describedby="${id}-help"` : ''}>
            <span class="hb-checkbox__text">${field.checkboxLabel || field.label}</span>
          </label>
          ${field.helpText ? `<p id="${id}-help" class="hb-checkbox__help">${field.helpText}</p>` : ''}
        </div>
      `;
    }

    renderRadioGroup(field) {
      const options = field.options || [];
      const name = `hb-radio-${field.id}`;

      const optionsHtml = options.map(opt => `
        <label class="hb-radio__option">
          <input type="radio" name="${name}" value="${opt.value}"
                 class="hb-radio__input"
                 ${field.required ? `required` : ''}>
          <span class="hb-radio__content">
            <span class="hb-radio__label">${opt.label}</span>
            ${opt.description ? `<span class="hb-radio__desc">${opt.description}</span>` : ''}
          </span>
        </label>
      `).join('');

      return `
        <fieldset class="hb-radio-group" data-component="radioGroup" data-field-id="${field.id}">
          <legend class="hb-radio-group__legend">${field.label}</legend>
          ${optionsHtml}
        </fieldset>
      `;
    }

    renderFileUpload(field) {
      const maxFiles = field.maxFiles || 5;
      // Native Shopify upload — always available, no third-party config needed.
      const disabled = false;
      const disabledClass = '';
      const disabledAttr = '';

      // Restore previously uploaded files
      const existingFiles = this._uploadedFiles && this._uploadedFiles[field.id] || [];
      const previewsHtml = existingFiles.map((f, i) => `
        <div class="hb-upload__preview" data-file-index="${i}">
          <img src="${f.localUrl || ''}" alt="${this._escapeHtml(f.name)}" class="hb-upload__preview-img">
          <div class="hb-upload__preview-info">
            <span class="hb-upload__preview-name">${this._escapeHtml(f.name)}</span>
          </div>
          <button type="button" class="hb-upload__preview-remove" aria-label="Remove ${this._escapeHtml(f.name)}" data-file-index="${i}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
      `).join('');

      return `
        <div class="hb-upload${disabledClass}" data-component="fileUpload" data-field-id="${field.id}" data-max-files="${maxFiles}">
          <label for="hb-upload-${field.id}" class="hb-upload__label">
            <span class="hb-upload__label-text">${field.label}</span>
            <span class="hb-upload__optional">(Optional)</span>
          </label>

          <div class="hb-upload__dropzone${disabled ? ' hb-upload__dropzone--disabled' : ''}"
               role="button"
               tabindex="0"
               ${disabledAttr}
               aria-label="${disabled ? 'File upload not configured' : 'Upload files. Drag and drop or click to browse.'}">
            <input type="file"
                   id="hb-upload-${field.id}"
                   class="hb-upload__input visually-hidden"
                   accept="image/jpeg,image/png,image/webp"
                   multiple
                   ${disabled ? 'disabled' : ''}
                   aria-hidden="true">
            <div class="hb-upload__placeholder">
              <svg class="hb-upload__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <span class="hb-upload__text">
                ${disabled
                  ? 'File upload not configured'
                  : '<strong>Drag &amp; drop</strong> photos here or <strong>click to browse</strong>'}
              </span>
              <span class="hb-upload__hint">${disabled
                ? 'Contact store owner to enable photo uploads'
                : (field.helpText || 'Up to ' + maxFiles + ' images (JPEG, PNG, WebP) — Max 10MB each')}</span>
            </div>
          </div>

          <div class="hb-upload__previews" ${existingFiles.length ? '' : 'hidden'}>
            ${previewsHtml}
          </div>

          <div class="hb-upload__status" aria-live="polite" hidden></div>
        </div>
      `;
    }

    // =========================================================================
    // Conditional Field Visibility
    // =========================================================================

    updateConditionalFields() {
      // Handle multiField showWhen conditions
      const conditionalFields = this.els.content.querySelectorAll('[data-show-when]');
      conditionalFields.forEach(wrapper => {
        // Cache parsed JSON on the element to avoid re-parsing
        if (!wrapper._parsedCondition) {
          wrapper._parsedCondition = JSON.parse(wrapper.dataset.showWhen);
        }
        const shouldShow = this.checkCondition(wrapper._parsedCondition);
        wrapper.hidden = !shouldShow;

        // Clear selection if hiding
        if (!shouldShow) {
          const fieldId = wrapper.dataset.fieldWrapper;
          if (fieldId) delete this.selections[fieldId];
        }
      });

      // Handle crown step conditionalFields (brick initial, etc.)
      const stepConditionals = this.els.content.querySelectorAll('.hat-builder__conditional-field');
      stepConditionals.forEach(cf => {
        const parentField = cf.dataset.showWhenParent;
        if (!cf._parsedValues) {
          cf._parsedValues = JSON.parse(cf.dataset.showWhenValues || '[]');
        }
        const currentVal = this.selections[parentField];
        const shouldShow = cf._parsedValues.includes(currentVal);
        cf.hidden = !shouldShow;

        if (!shouldShow) {
          const fieldId = cf.dataset.fieldWrapper;
          if (fieldId) delete this.selections[fieldId];
        }
      });
    }

    // =========================================================================
    // Selection State Management
    // =========================================================================

    applySelections(step) {
      if (step.type === 'multiField' && step.fields) {
        // Restore all sub-field selections
        step.fields.forEach(field => {
          const savedValue = this.selections[field.id];
          if (!savedValue) {
            // Apply default values
            if (field.defaultValue) {
              this.selections[field.id] = field.defaultValue;
              this._applyFieldSelection(field.id, field.defaultValue, field.type);
            }
            return;
          }
          this._applyFieldSelection(field.id, savedValue, field.type);
        });
      } else {
        const savedValue = this.selections[step.id];
        if (!savedValue) return;
        this._applyFieldSelection(step.id, savedValue, step.type);
      }

      // Also restore conditional field values (e.g., brick initial)
      if (step.conditionalFields) {
        step.conditionalFields.forEach(cf => {
          const fieldId = cf.field.id;
          const savedValue = this.selections[fieldId];
          if (savedValue) {
            this._applyFieldSelection(fieldId, savedValue, cf.field.type);
          }
        });
      }
    }

    _applyFieldSelection(fieldId, value, type) {
      const wrapper = this.els.content.querySelector(`[data-field-id="${fieldId}"]`);
      if (!wrapper) return;

      if (type === 'buttonGroup' || type === 'thumbnailPicker' || type === 'swatchPicker') {
        const option = wrapper.querySelector(`[data-value="${value}"]`);
        if (option) {
          option.setAttribute('aria-checked', 'true');

          // Auto-switch category tab if selected option is in a hidden panel
          const panel = option.closest('[role="tabpanel"]');
          if (panel && panel.hidden) {
            const thumbnailsContainer = panel.closest('.hb-thumbnails');
            if (thumbnailsContainer) {
              thumbnailsContainer.querySelectorAll('[role="tabpanel"]').forEach(p => p.hidden = true);
              panel.hidden = false;
              const tabId = panel.getAttribute('aria-labelledby');
              thumbnailsContainer.querySelectorAll('[role="tab"]').forEach(t => {
                t.setAttribute('aria-selected', t.id === tabId ? 'true' : 'false');
              });
            }
          }
        }
        // Update swatch display
        if (type === 'swatchPicker') {
          const display = wrapper.querySelector('.hb-swatches__selected-value');
          if (display) {
            const label = option ? option.getAttribute('aria-label') : value;
            display.textContent = label;
          }
        }
      }
      // Text, textarea, dropdown, checkbox, radio are handled by bindStepEvents() value restoration
    }

    // =========================================================================
    // Event Binding
    // =========================================================================

    bindEvents() {
      // Navigation buttons
      this.els.backBtn.addEventListener('click', () => this.goBack());
      this.els.nextBtn.addEventListener('click', () => this.goNext());
      this.els.addToCartBtn.addEventListener('click', () => this.addToCart());

      // Keep variantId in sync when customer changes hat size
      // The product template sets container.dataset.variantId on change
      const observer = new MutationObserver(() => {
        const newId = parseInt(this.container.dataset.variantId, 10);
        if (newId && newId !== this.config.variantId) {
          this.config.variantId = newId;
          console.log('[HatBuilder] Variant synced:', newId);
        }
      });
      observer.observe(this.container, { attributes: true, attributeFilter: ['data-variant-id'] });

      // Reverse sync: when the customer changes the visible top-of-page
      // dropdown, mirror the value into selections.hatSize so it stays
      // authoritative. This also fires when sessionStorage restores the
      // dropdown on bfcache return (the inline script dispatches change), so
      // the customer's previous choice is recovered automatically.
      const sizeDropdown = document.querySelector('.hat-product__variant-select');
      if (sizeDropdown) {
        sizeDropdown.addEventListener('change', () => {
          const value = sizeDropdown.options[sizeDropdown.selectedIndex]?.value;
          if (value) {
            this.selections.hatSize = String(value);
            console.log('[HatBuilder] selections.hatSize synced from dropdown change:', value);
          }
        });
      }

      // Handle bfcache resume (back/forward navigation or returning to tab)
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          // Page was restored from bfcache — reset any stuck UI state
          const btn = this.els.addToCartBtn;
          if (btn && btn.disabled) {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            btn.textContent = 'Add to Cart';
          }
        }
      });

      // Stepper clicks
      this.els.stepper.addEventListener('click', (e) => {
        const btn = e.target.closest('.hat-builder-stepper__button');
        if (!btn || btn.disabled) return;

        const stepItem = btn.closest('.hat-builder-stepper__step');
        const index = parseInt(stepItem.dataset.stepIndex, 10);
        if (!isNaN(index)) {
          this.renderStep(index);
        }
      });

      // Keyboard navigation for stepper
      this.els.stepper.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          const direction = e.key === 'ArrowRight' ? 1 : -1;
          const newIndex = Math.max(0, Math.min(this.currentStep + direction, this.steps.length - 1));
          const stepBtn = this.els.stepper.querySelector(`[data-step-index="${newIndex}"] .hat-builder-stepper__button`);
          if (stepBtn && !stepBtn.disabled) {
            stepBtn.focus();
            this.renderStep(newIndex);
          }
        }
      });
    }

    bindStepEvents() {
      const step = this.steps[this.currentStep];
      if (!step) return;

      // Radio group behavior for buttons/thumbnails/swatches (ARIA radio pattern)
      const radioGroups = this.els.content.querySelectorAll('[role="radiogroup"]');

      radioGroups.forEach(group => {
        const fieldId = group.dataset.fieldId;
        const options = group.querySelectorAll('[role="radio"]');

        options.forEach(option => {
          option.addEventListener('click', () => {
            // Deselect all radios in this group (across all category panels)
            const allRadios = group.querySelectorAll('[role="radio"]');
            allRadios.forEach(o => o.setAttribute('aria-checked', 'false'));

            // Select this one
            option.setAttribute('aria-checked', 'true');

            // Save selection and clear any validation error
            const value = option.dataset.value;
            this.selections[fieldId] = value;
            this._clearValidationError();

            // Drive the hidden native variant <select> so cart / price / URL
            // stay in sync when the customer picks a size inside the builder.
            if (fieldId === 'hatSize') {
              this._syncHatSizeToVariantSelect(value);
            }

            // Handle special actions
            const action = option.dataset.action;
            if (action === 'skipToCart') {
              this.skipToCart();
              return;
            }
            if (action === 'skipToPhotos') {
              this.skipToPhotos();
              return;
            }

            // When shapeMethod changes, step visibility changes — rebuild
            // stepper and conditional fields to reflect the new path.
            if (fieldId === 'shapeMethod') {
              this.buildStepper();
              this.updateStepperState();
              this.updateConditionalFields();
            }

            // Update swatch display
            if (group.dataset.component === 'swatches' || group.classList.contains('hb-swatches')) {
              const label = option.getAttribute('aria-label');
              const display = group.querySelector('.hb-swatches__selected-value');
              if (display) display.textContent = label;
            }

            // Update conditional fields
            this.updateConditionalFields();

            // Calculate pricing
            this.calculatePrice();

            // Announce selection
            const label = option.querySelector('.hb-button-opt__label, .hb-thumbnail__label')?.textContent || option.getAttribute('aria-label');
            this.announce('Selected: ' + label);

            // Auto-advance for standalone steps (not sub-fields in multiField)
            // Only for button groups and thumbnails, not swatches (which may have companion fields)
            const isMultiField = step.type === 'multiField';
            const isButtonOrThumb = group.dataset.component === 'buttons' || group.dataset.component === 'thumbnails';
            if (!isMultiField && isButtonOrThumb) {
              clearTimeout(this._autoAdvanceTimer);
              this._autoAdvanceTimer = setTimeout(() => {
                this.goNext();
              }, 500);
            }

            // MultiField: update progress indicator
            if (isMultiField) {
              this._updateMultiFieldProgress();
            }
          });

          // Keyboard support
          option.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              option.click();
            }
          });
        });
      });

      // Category tab switching
      const categoryTabs = this.els.content.querySelectorAll('.hb-thumbnails__category-tab');
      categoryTabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const thumbnailsContainer = tab.closest('.hb-thumbnails');

          // Update tab states
          thumbnailsContainer.querySelectorAll('.hb-thumbnails__category-tab').forEach(t =>
            t.setAttribute('aria-selected', 'false')
          );
          tab.setAttribute('aria-selected', 'true');

          // Show/hide panels
          thumbnailsContainer.querySelectorAll('[role="tabpanel"]').forEach(p => p.hidden = true);
          const targetPanel = thumbnailsContainer.querySelector(`#${tab.getAttribute('aria-controls')}`);
          if (targetPanel) targetPanel.hidden = false;
        });
      });

      // Text fields
      this.els.content.querySelectorAll('[data-component="textField"] input').forEach(input => {
        const fieldId = input.closest('[data-field-id]').dataset.fieldId;

        // Restore saved value
        if (this.selections[fieldId]) input.value = this.selections[fieldId];

        input.addEventListener('input', () => {
          this.selections[fieldId] = input.value;
          this._clearValidationError();
          // Update character count
          const countEl = input.closest('[data-component="textField"]').querySelector('[data-count]');
          if (countEl) countEl.textContent = input.value.length;
          this.updateConditionalFields();
          // Debounce price calculation during typing
          clearTimeout(this._priceDebounce);
          this._priceDebounce = setTimeout(() => this.calculatePrice(), 300);
        });
      });

      // Textareas
      this.els.content.querySelectorAll('[data-component="textArea"] textarea').forEach(textarea => {
        const fieldId = textarea.closest('[data-field-id]').dataset.fieldId;

        // Restore saved value
        if (this.selections[fieldId]) textarea.value = this.selections[fieldId];

        textarea.addEventListener('input', () => {
          this.selections[fieldId] = textarea.value;
          this._clearValidationError();
          const countEl = textarea.closest('[data-component="textArea"]').querySelector('[data-count]');
          if (countEl) countEl.textContent = textarea.value.length;
        });
      });

      // Dropdowns
      this.els.content.querySelectorAll('[data-component="dropdown"] select').forEach(select => {
        const fieldId = select.closest('[data-field-id]').dataset.fieldId;

        // Restore saved value
        if (this.selections[fieldId]) {
          select.value = this.selections[fieldId];
        } else {
          // Set default from selected option
          const defaultOpt = select.querySelector('option[selected]');
          if (defaultOpt && defaultOpt.value) {
            this.selections[fieldId] = defaultOpt.value;
          }
        }

        select.addEventListener('change', () => {
          this.selections[fieldId] = select.value;
          this._clearValidationError();
          this.calculatePrice();
          this.updateConditionalFields();

          // MultiField: update progress indicator
          if (step.type === 'multiField') {
            this._updateMultiFieldProgress();
          }
        });
      });

      // Checkboxes
      this.els.content.querySelectorAll('[data-component="checkbox"] input[type="checkbox"]').forEach(checkbox => {
        const fieldId = checkbox.closest('[data-field-id]').dataset.fieldId;

        // Restore saved value
        if (this.selections[fieldId]) checkbox.checked = this.selections[fieldId] === 'Yes';

        checkbox.addEventListener('change', () => {
          this.selections[fieldId] = checkbox.checked ? 'Yes' : '';
          this._clearValidationError();
          this.updateConditionalFields();
        });
      });

      // Radio groups (native HTML radios)
      this.els.content.querySelectorAll('[data-component="radioGroup"]').forEach(fieldset => {
        const fieldId = fieldset.dataset.fieldId;

        fieldset.querySelectorAll('input[type="radio"]').forEach(radio => {
          // Restore saved value
          if (this.selections[fieldId] === radio.value) radio.checked = true;

          radio.addEventListener('change', () => {
            this.selections[fieldId] = radio.value;
            this.calculatePrice();
            this.updateConditionalFields();
          });
        });
      });

      // File upload components
      this.els.content.querySelectorAll('[data-component="fileUpload"]').forEach(uploadEl => {
        const fieldId = uploadEl.dataset.fieldId;
        const maxFiles = parseInt(uploadEl.dataset.maxFiles) || 5;
        const dropzone = uploadEl.querySelector('.hb-upload__dropzone');
        const fileInput = uploadEl.querySelector('.hb-upload__input');
        const previewsContainer = uploadEl.querySelector('.hb-upload__previews');
        const statusEl = uploadEl.querySelector('.hb-upload__status');

        if (!fileInput || fileInput.disabled) return;

        // Initialize upload storage for this field
        if (!this._uploadedFiles) this._uploadedFiles = {};
        if (!this._uploadedFiles[fieldId]) this._uploadedFiles[fieldId] = [];

        const handleFiles = (files) => {
          const currentCount = this._uploadedFiles[fieldId].length;
          const remaining = maxFiles - currentCount;
          if (remaining <= 0) {
            this._showUploadStatus(statusEl, `Maximum ${maxFiles} files already uploaded`, 'error');
            return;
          }

          const filesToProcess = Array.from(files).slice(0, remaining);
          const maxSizeMB = 10;

          filesToProcess.forEach(file => {
            // Validate type
            if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
              this._showUploadStatus(statusEl, `${file.name}: Only JPEG, PNG, and WebP images are allowed`, 'error');
              return;
            }
            // Validate size
            if (file.size > maxSizeMB * 1024 * 1024) {
              this._showUploadStatus(statusEl, `${file.name}: File exceeds ${maxSizeMB}MB limit`, 'error');
              return;
            }

            this._addLocalFile(file, fieldId, previewsContainer, statusEl);
          });
        };

        // Click to browse
        dropzone.addEventListener('click', () => fileInput.click());
        dropzone.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInput.click();
          }
        });

        // File input change
        fileInput.addEventListener('change', () => {
          if (fileInput.files.length) handleFiles(fileInput.files);
          fileInput.value = '';
        });

        // Drag and drop
        dropzone.addEventListener('dragover', (e) => {
          e.preventDefault();
          dropzone.classList.add('is-dragover');
        });
        dropzone.addEventListener('dragleave', () => {
          dropzone.classList.remove('is-dragover');
        });
        dropzone.addEventListener('drop', (e) => {
          e.preventDefault();
          dropzone.classList.remove('is-dragover');
          if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
        });

        // Remove buttons (delegated)
        previewsContainer.addEventListener('click', (e) => {
          const removeBtn = e.target.closest('.hb-upload__preview-remove');
          if (!removeBtn) return;

          const index = parseInt(removeBtn.dataset.fileIndex);
          const files = this._uploadedFiles[fieldId];
          const removed = files.splice(index, 1)[0];
          if (removed && removed.localUrl) URL.revokeObjectURL(removed.localUrl);

          // Update selection
          this.selections[fieldId] = files.map(f => f.name).join(', ');
          if (!files.length) {
            delete this.selections[fieldId];
            previewsContainer.hidden = true;
          }

          // Re-render previews
          this._renderUploadPreviews(fieldId, previewsContainer);
          this.announce(`Removed ${removed.name}`);
        });
      });

      // Dismissible prefill banner
      const bannerClose = this.els.content.querySelector('.hb-prefill-banner__close');
      if (bannerClose) {
        bannerClose.addEventListener('click', () => {
          const banner = bannerClose.closest('.hb-prefill-banner');
          if (banner) {
            banner.style.opacity = '0';
            banner.style.transform = 'translateY(-8px)';
            setTimeout(() => banner.remove(), 300);
          }
          this._prefillMode = false;
        });
      }

    }

    // =========================================================================
    // Navigation
    // =========================================================================

    updateNavigation() {
      // Back button
      this.els.backBtn.disabled = this.currentStep === 0;

      // Next/Add to Cart
      const isLastStep = this.currentStep === this.steps.length - 1;
      this.els.nextBtn.hidden = isLastStep;
      this.els.addToCartBtn.hidden = !isLastStep;

      // Update total display
      this.updateTotalDisplay();
    }

    // Step-level visibility — a step is hidden if its showWhen condition is
    // not currently satisfied. Also auto-hides multiField steps where every
    // sub-field is hidden (catches the "empty Brim Style" case on straws in
    // photo mode).
    _isStepVisible(step) {
      if (!step) return false;
      if (step.showWhen && !this.checkCondition(step.showWhen)) return false;
      if (step.type === 'multiField' && Array.isArray(step.fields) && step.fields.length > 0) {
        const anyVisible = step.fields.some(f => !f.showWhen || this.checkCondition(f.showWhen));
        if (!anyVisible) return false;
      }
      return true;
    }

    // Put the photo upload right after shapeMethod so it's the VERY NEXT step
    // after the customer picks "Match a Reference Photo". Head Shape (anatomical
    // fit, still required) follows immediately after the upload.
    _reorderStepsForPhotoPath() {
      const photosIdx = this.steps.findIndex(s => s.id === 'notesAndPhotos');
      if (photosIdx < 0) return;
      const [photosStep] = this.steps.splice(photosIdx, 1);
      const smIdx = this.steps.findIndex(s => s.id === 'shapeMethod');
      if (smIdx >= 0) {
        this.steps.splice(smIdx + 1, 0, photosStep);
      } else {
        const hsIdx = this.steps.findIndex(s => s.id === 'headShape');
        this.steps.splice(Math.max(0, hsIdx) + 1, 0, photosStep);
      }
    }

    _findNextVisibleStep(fromIndex) {
      for (let i = fromIndex + 1; i < this.steps.length; i++) {
        if (this._isStepVisible(this.steps[i])) return i;
      }
      return -1;
    }

    _findPrevVisibleStep(fromIndex) {
      for (let i = fromIndex - 1; i >= 0; i--) {
        if (this._isStepVisible(this.steps[i])) return i;
      }
      return -1;
    }

    goBack() {
      clearTimeout(this._autoAdvanceTimer);
      const prev = this._findPrevVisibleStep(this.currentStep);
      if (prev >= 0) {
        this.renderStep(prev);
        this.announce('Step ' + (this.currentStep + 1) + ' of ' + this.steps.length + ': ' + this.steps[this.currentStep].title);
      }
    }

    goNext() {
      clearTimeout(this._autoAdvanceTimer);
      const step = this.steps[this.currentStep];

      // Validate current step
      if (!this._validateStep(step)) return;

      const next = this._findNextVisibleStep(this.currentStep);
      if (next >= 0) {
        this.renderStep(next);
        this.announce('Step ' + (this.currentStep + 1) + ' of ' + this.steps.length + ': ' + this.steps[this.currentStep].title);
      }
    }

    skipToCart() {
      // When the customer chooses "Keep As-Is," strip any prefilled or
      // previously-entered shaping/customization selections so they don't
      // leak into the order. Preserve only what's still relevant for an
      // unshaped hat: the size (variant), the customize choice itself,
      // confirmation-step fields, and any notes they may want to leave.
      //
      // Without this clear, a customer who arrived via the Hat Gallery
      // (which prefills selections from URL params) or who first picked
      // "Yes" and then changed their mind would end up with shape data
      // attached to an order they said should ship unshaped.
      const keepIds = ['hatSize', 'customize', 'sizeConfirmation', 'requestFitGuide', 'premium30xAcknowledgment', 'notes'];
      const preserved = {};
      for (const id of keepIds) {
        if (this.selections[id] !== undefined) preserved[id] = this.selections[id];
      }
      this.selections = preserved;
      this.selections['customize'] = 'no';

      // Jump to confirmation step so they verify size & confirmation fields
      const confirmIdx = this.steps.findIndex(s => s.id === 'confirmation');
      if (confirmIdx !== -1) {
        this.renderStep(confirmIdx);
      } else {
        this.addToCart();
      }
    }

    skipToPhotos() {
      // Customer chose "Match a Reference Photo" — strip any shape-picker
      // selections (so they don't leak into the order alongside the photo).
      // Crown / brim front / brim shape steps and fields are auto-hidden via
      // showWhen. _reorderStepsForPhotoPath moves the photo upload to right
      // after this step, so we advance straight to the upload. Head Shape (an
      // anatomical fit question, still required) follows immediately after.
      const keepIds = [
        'hatSize', 'customize', 'shapeMethod', 'headShape',
        'brimSize',
        'ribbonType', 'ribbonColor', 'secondRibbonColor', 'boundEdge', 'boundEdgeColor',
        'wantBranding', 'brandingText', 'brandingPlacement', 'brandingConfirm',
        'notes', 'photos',
        'sizeConfirmation', 'requestFitGuide', 'premium30xAcknowledgment'
      ];
      const preserved = {};
      for (const id of keepIds) {
        if (this.selections[id] !== undefined) preserved[id] = this.selections[id];
      }
      this.selections = preserved;
      this.selections['shapeMethod'] = 'photo';

      // Reorder so notesAndPhotos sits right after shapeMethod — the customer
      // uploads immediately, then answers the Head Shape fit question next.
      this._reorderStepsForPhotoPath();

      this.buildStepper();
      this.updateStepperState();

      const next = this._findNextVisibleStep(this.currentStep);
      if (next >= 0) {
        this.renderStep(next);
      } else {
        const confirmIdx = this.steps.findIndex(s => s.id === 'confirmation');
        if (confirmIdx !== -1) this.renderStep(confirmIdx);
      }
    }

    // =========================================================================
    // Validation
    // =========================================================================

    _validateStep(step) {
      // Clear any previous error
      this._clearValidationError();

      // Hidden steps don't need validation — they're not visible to the customer.
      if (!this._isStepVisible(step)) return true;

      // notesAndPhotos: photos become required when shapeMethod = 'photo'.
      if (step.id === 'notesAndPhotos' && this.selections.shapeMethod === 'photo') {
        if (!this.selections.photos || this.selections.photos === '') {
          this._showValidationError('Please upload at least one reference photo — we use it to shape your hat.');
          return false;
        }
      }

      if (step.type === 'multiField' && step.fields) {
        // Always check visible required sub-fields, even if the step itself is optional
        for (const field of step.fields) {
          // Skip fields hidden by conditions
          if (field.showWhen && !this.checkCondition(field.showWhen)) continue;
          if (field.required && !this.selections[field.id]) {
            this._showValidationError('Please select ' + field.label + ' to continue.');
            return false;
          }
        }
        return true;
      }

      if (!step.required) return true;

      // Single-field step
      if (!this.selections[step.id]) {
        this._showValidationError('Please make a selection for ' + step.title + ' to continue.');
        return false;
      }

      // Also validate conditional fields (e.g., brick initial)
      if (step.conditionalFields) {
        for (const cf of step.conditionalFields) {
          if (cf.showWhen && cf.showWhen.optionValue &&
              cf.showWhen.optionValue.includes(this.selections[step.id])) {
            if (cf.field.required && !this.selections[cf.field.id]) {
              this._showValidationError('Please enter ' + cf.field.label + ' to continue.');
              return false;
            }
          }
        }
      }

      return true;
    }

    _showValidationError(message) {
      this.announce(message);
      this.els.content.classList.add('shake');
      setTimeout(() => this.els.content.classList.remove('shake'), 500);

      // Show visible error message
      let errorEl = this.els.content.querySelector('.hb-validation-error');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'hb-validation-error';
        errorEl.setAttribute('role', 'alert');
        this.els.content.appendChild(errorEl);
      }
      errorEl.textContent = message;
      errorEl.hidden = false;

      // Scroll error into view on mobile
      setTimeout(() => errorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    }

    _clearValidationError() {
      const errorEl = this.els.content?.querySelector('.hb-validation-error');
      if (errorEl) errorEl.hidden = true;
    }

    // =========================================================================
    // Pricing
    // =========================================================================

    calculatePrice() {
      let upgradeTotal = 0;
      this.upgrades = [];

      this.steps.forEach(step => {
        if (step.type === 'multiField' && step.fields) {
          step.fields.forEach(field => {
            this._calculateFieldPrice(field);
          });
        } else if (step.options) {
          this._calculateFieldPrice(step);
        }
      });

      // Sum upgrades
      this.upgrades.forEach(u => upgradeTotal += u.price);

      // Update displays
      this._updatePriceDisplays(upgradeTotal);

      // Update live selections summary (debounced to avoid double-render)
      if (!this._summaryPending) {
        this._summaryPending = true;
        requestAnimationFrame(() => {
          this._summaryPending = false;
          this._renderSelectionsSummary();
        });
      }
    }

    _calculateFieldPrice(field) {
      const selected = this.selections[field.id];
      if (!selected) return;

      // Check for options with price
      const options = field.options || this.getLibraryOptions(field.optionsRef) || [];
      const selectedOpt = options.find(o => o.value === selected);
      if (selectedOpt && selectedOpt.price && selectedOpt.price > 0) {
        this.upgrades.push({
          name: (field.label || field.title) + ': ' + selectedOpt.label,
          price: selectedOpt.price * 100
        });
      }

      // Check for tiered pricing (branding text)
      if (field.pricing && field.pricing.type === 'tiered' && selected) {
        const charCount = selected.length;
        if (charCount > 0) {
          let price = 0;
          for (const tier of field.pricing.tiers) {
            if (charCount < tier.from) continue;
            if (tier.price) {
              // Flat price for this tier range
              price += tier.price;
            }
            if (tier.pricePerUnit) {
              // Per-unit price for chars in this range
              const charsInTier = Math.max(0, Math.min(charCount, tier.to) - tier.from + 1);
              if (charsInTier > 0) price += charsInTier * tier.pricePerUnit;
            }
          }
          if (price > 0) {
            this.upgrades.push({
              name: `Branding: "${selected}" (${charCount} letters)`,
              price: price * 100
            });
          }
        }
      }
    }

    _updatePriceDisplays(upgradeTotal) {
      const basePrice = this.config.basePrice;
      const total = basePrice + upgradeTotal;

      if (this.els.summary) {
        const baseEl = this.els.summary.querySelector('[data-base-price]');
        const upgradesEl = this.els.summary.querySelector('[data-upgrades-price]');
        const totalEl = this.els.summary.querySelector('[data-total-price]');
        const upgradesRow = this.els.summary.querySelector('.hb-summary__upgrades');

        if (baseEl) baseEl.textContent = this.formatMoney(basePrice);
        if (upgradesEl) upgradesEl.textContent = '+' + this.formatMoney(upgradeTotal);
        if (totalEl) totalEl.textContent = this.formatMoney(total);
        if (upgradesRow) upgradesRow.hidden = upgradeTotal === 0;
      }

      this.updateTotalDisplay();
    }

    updateTotalDisplay() {
      let total = this.config.basePrice;
      this.upgrades.forEach(u => total += u.price);

      if (this.els.totalDisplay) {
        this.els.totalDisplay.textContent = this.formatMoney(total);
      }
    }

    // =========================================================================
    // Confirmation Summary
    // =========================================================================

    _renderSelectionsSummary() {
      const selectionsContainer = this.els.summary?.querySelector('.hb-summary__selections');
      const selectionsList = this.els.summary?.querySelector('.hb-summary__list');
      if (!selectionsContainer || !selectionsList) return;

      const editIcon = '<svg class="hb-summary__edit-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
      const checkSvg = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

      // hatSize is rendered separately and pinned to the top \u2014 guaranteed to
      // appear even if the step wasn't injected for this product.
      // Hide shape-picker rows when the customer chose the photo path.
      const photoMode = this.selections.shapeMethod === 'photo';
      const narrativeFields = [
        { stepId: 'shapeMethod', fieldId: null, label: 'Shape Method' },
        { stepId: 'headShape', fieldId: null, label: 'Head Shape' },
        ...(photoMode ? [] : [
          { stepId: 'crownShape', fieldId: null, label: 'Crown Shape' },
        ]),
        { stepId: 'brimStyle', fieldId: 'brimSize', label: 'Brim Size' },
        ...(photoMode ? [] : [
          { stepId: 'brimStyle', fieldId: 'brimShape', label: 'Brim Shape' },
          { stepId: 'brimStyle', fieldId: 'brimFront', label: 'Brim Front' },
        ]),
        { stepId: 'ribbon', fieldId: 'ribbonType', label: 'Ribbon' },
        { stepId: 'ribbon', fieldId: 'boundEdge', label: 'Edge' },
        { stepId: 'branding', fieldId: 'wantBranding', label: 'Branding' }
      ];

      let listHtml = '';

      // Hat Size always at the top of the sidebar \u2014 resolved from selections
      // or current variant. Mirrors the confirmation review block.
      const hatSizeLabel = this._resolveHatSizeLabel();
      const hatSizeStepIdx = this.steps.findIndex(s => s.id === 'hatSize');
      const hatSizeRowTag = hatSizeLabel
        ? `<span class="hb-summary__price-tag hb-summary__price-tag--included">Selected ${checkSvg}</span>`
        : '';
      const hatSizeRowOpen = hatSizeStepIdx >= 0
        ? `<button type="button" class="hb-summary__item" data-goto-step="${hatSizeStepIdx}" aria-label="Edit Hat Size">`
        : `<div class="hb-summary__item hb-summary__item--fixed">`;
      const hatSizeRowClose = hatSizeStepIdx >= 0 ? `</button>` : `</div>`;
      listHtml += `
        ${hatSizeRowOpen}
          <dt class="hb-summary__item-label">Hat Size</dt>
          <dd class="hb-summary__item-value">
            <span class="hb-summary__item-selection">${hatSizeLabel || '\u2014'}</span>
            ${hatSizeRowTag}
            ${hatSizeStepIdx >= 0 ? editIcon : ''}
          </dd>
        ${hatSizeRowClose}
      `;

      narrativeFields.forEach(entry => {
        const stepIndex = this.steps.findIndex(s => s.id === entry.stepId);
        if (stepIndex === -1) return;
        const step = this.steps[stepIndex];

        // Resolve field definition and current value
        let field, value;
        if (entry.fieldId) {
          field = step.fields?.find(f => f.id === entry.fieldId);
          if (!field) return;
          value = this.selections[field.id];
        } else {
          field = step;
          value = this.selections[step.id];
        }

        const hasValue = value && value !== '' && value !== 'none' && value !== 'no';
        const isReached = stepIndex <= this.currentStep;
        const isOptional = !step.required;

        // Determine display text and price tag
        let displayText = '\u2014';
        let priceTagHtml = '';
        let rowModifier = '';

        if (hasValue) {
          displayText = this._getFieldSelectionLabel(field, value);
          const status = this._getSelectionPriceStatus(field, value);
          if (status.type === 'upgrade') {
            priceTagHtml = `<span class="hb-summary__price-tag hb-summary__price-tag--upgrade">+${this.formatMoney(status.price)}</span>`;
          } else {
            priceTagHtml = `<span class="hb-summary__price-tag hb-summary__price-tag--included">Included ${checkSvg}</span>`;
          }
        } else if (isOptional && isReached) {
          priceTagHtml = '<span class="hb-summary__price-tag hb-summary__price-tag--optional">Optional</span>';
          rowModifier = ' hb-summary__item--dimmed';
        } else if (!isReached) {
          rowModifier = ' hb-summary__item--pending';
        }

        listHtml += `
          <button type="button" class="hb-summary__item${rowModifier}" data-goto-step="${stepIndex}" aria-label="Edit ${entry.label}">
            <dt class="hb-summary__item-label">${entry.label}</dt>
            <dd class="hb-summary__item-value">
              <span class="hb-summary__item-selection">${displayText}</span>
              ${priceTagHtml}
              ${editIcon}
            </dd>
          </button>
        `;

        // Show conditional fields inline (e.g., brick initial for crown)
        if (!entry.fieldId && step.conditionalFields) {
          step.conditionalFields.forEach(cf => {
            const cfVal = this.selections[cf.field.id];
            if (cfVal) {
              listHtml += `
                <button type="button" class="hb-summary__item" data-goto-step="${stepIndex}" aria-label="Edit ${cf.field.label}">
                  <dt class="hb-summary__item-label">${cf.field.label}</dt>
                  <dd class="hb-summary__item-value">
                    <span class="hb-summary__item-selection">${cfVal}</span>
                    <span class="hb-summary__price-tag hb-summary__price-tag--included">Included ${checkSvg}</span>
                    ${editIcon}
                  </dd>
                </button>
              `;
            }
          });
        }

        // Show dependent sub-fields (ribbon color, bound edge color, branding text)
        if (entry.fieldId && step.fields) {
          const dependentFields = step.fields.filter(f =>
            f.id !== entry.fieldId && f.showWhen && f.showWhen[entry.fieldId]
          );
          dependentFields.forEach(depField => {
            const depVal = this.selections[depField.id];
            if (!depVal) return;
            const showValues = depField.showWhen[entry.fieldId];
            if (!showValues.includes(value)) return;
            const depLabel = this._getFieldSelectionLabel(depField, depVal);
            const depStatus = this._getSelectionPriceStatus(depField, depVal);
            let depTag = `<span class="hb-summary__price-tag hb-summary__price-tag--included">Included ${checkSvg}</span>`;
            if (depStatus.type === 'upgrade') {
              depTag = `<span class="hb-summary__price-tag hb-summary__price-tag--upgrade">+${this.formatMoney(depStatus.price)}</span>`;
            }
            listHtml += `
              <button type="button" class="hb-summary__item" data-goto-step="${stepIndex}" aria-label="Edit ${depField.label}">
                <dt class="hb-summary__item-label">${depField.label}</dt>
                <dd class="hb-summary__item-value">
                  <span class="hb-summary__item-selection">${depLabel}</span>
                  ${depTag}
                  ${editIcon}
                </dd>
              </button>
            `;
          });
        }
      });

      selectionsList.innerHTML = listHtml;

      // Use event delegation (one listener, never re-bound)
      if (!selectionsList._delegated) {
        selectionsList._delegated = true;
        selectionsList.addEventListener('click', (e) => {
          const item = e.target.closest('[data-goto-step]');
          if (!item) return;
          const idx = parseInt(item.dataset.gotoStep, 10);
          if (!isNaN(idx) && idx < this.steps.length) {
            this.renderStep(idx);
            this.announce('Editing: ' + this.steps[idx].title);
          }
        });
      }
    }

    // =========================================================================
    // Add to Cart
    // =========================================================================

    async addToCart() {
      const step = this.steps[this.currentStep];

      // Validate final step
      if (step && !this._validateStep(step)) return;

      // Photo-driven path: photos are required.
      if (this.selections.shapeMethod === 'photo') {
        if (!this.selections.photos || this.selections.photos === '') {
          this._showValidationError('Please upload at least one reference photo before adding to cart.');
          const idx = this.steps.findIndex(s => s.id === 'notesAndPhotos');
          if (idx >= 0) this.renderStep(idx);
          return;
        }
        ['crownShape', 'brimFront', 'brimFrontCustom', 'brimShape', 'brimShapeCustom'].forEach(id => {
          delete this.selections[id];
        });
      }

      // SINGLE SOURCE OF TRUTH for hat size: the builder's own selection state
      // mapped against the variant table. The <select>.value and
      // container.dataset.variantId can drift apart under browser back / bfcache /
      // race conditions — if we resolve the variant ID from this.selections.hatSize
      // and write the note from the same source, they are guaranteed to agree.
      const properties = {};
      const hatSizeVariants = Array.isArray(this.config.hatSizeVariants) ? this.config.hatSizeVariants : [];

      if (hatSizeVariants.length >= 2) {
        let selectedSize = this.selections.hatSize;
        const jumpToSizeStep = () => {
          const idx = this.steps.findIndex(s => s.id === 'hatSize');
          if (idx >= 0) this.renderStep(idx);
        };

        // Defensive fallback: read directly from the visible dropdown — that's
        // what the customer SEES and last selected. More reliable than
        // dataset.variantId, which can be stale during bfcache restores or
        // race conditions between the inline change handler and the builder's
        // MutationObserver.
        if (!selectedSize) {
          const dropdown = document.querySelector('.hat-product__variant-select');
          if (dropdown && dropdown.selectedIndex >= 0) {
            const dropdownValue = dropdown.options[dropdown.selectedIndex].value;
            const matchedVariant = hatSizeVariants.find(v => String(v.option1) === String(dropdownValue));
            if (matchedVariant && matchedVariant.available) {
              selectedSize = String(matchedVariant.option1);
              this.selections.hatSize = selectedSize;
              console.log('[HatBuilder] Resolved hatSize from dropdown at cart-add:', selectedSize);
            }
          }
        }
        // Last-resort fallback to dataset.variantId
        if (!selectedSize) {
          const fallbackId = parseInt(this.container.dataset.variantId, 10) || this.config.variantId;
          const fallbackVariant = hatSizeVariants.find(v => Number(v.id) === Number(fallbackId));
          if (fallbackVariant && fallbackVariant.available && fallbackVariant.option1) {
            selectedSize = String(fallbackVariant.option1);
            this.selections.hatSize = selectedSize;
            console.log('[HatBuilder] Recovered hatSize from dataset at cart-add (last resort):', selectedSize);
          }
        }

        if (!selectedSize) {
          this._showValidationError('Please select a hat size before adding to cart.');
          jumpToSizeStep();
          return;
        }
        const chosenVariant = hatSizeVariants.find(v => String(v.option1) === String(selectedSize));
        if (!chosenVariant) {
          this._showValidationError('Hat size is invalid. Please re-select your size.');
          jumpToSizeStep();
          return;
        }
        if (!chosenVariant.available) {
          this._showValidationError('The selected size is sold out. Please pick another size.');
          jumpToSizeStep();
          return;
        }

        // Authoritative assignment — overrides any stale state from DOM/dataset.
        // We deliberately do NOT write properties['Hat Size'] — the variant
        // title already encodes the size, and Shopify's cart UI renders
        // variant + property both as "Hat Size: X", causing a duplicate row.
        this.config.variantId = chosenVariant.id;
        console.log('[HatBuilder] Resolved variant from selections.hatSize:', selectedSize, '→ variant', chosenVariant.id);
      }
      // Single-variant products: no size to write — variant title is the size.

      this.steps.forEach(s => {
        if (s.type === 'multiField' && s.fields) {
          s.fields.forEach(field => {
            // File uploads attach as real files in the multipart request below,
            // never as text properties here.
            if (field.type === 'fileUpload') return;
            if (field.lineItemProperty && this.selections[field.id]) {
              properties[field.lineItemProperty] = this._getFieldSelectionLabel(field, this.selections[field.id]);
            }
          });
        } else if (s.type !== 'fileUpload' && s.lineItemProperty && this.selections[s.id]) {
          properties[s.lineItemProperty] = this._getFieldSelectionLabel(s, this.selections[s.id]);
        }

        // Include conditional field properties
        if (s.conditionalFields) {
          s.conditionalFields.forEach(cf => {
            if (cf.field.type === 'fileUpload') return;
            if (cf.field.lineItemProperty && this.selections[cf.field.id]) {
              properties[cf.field.lineItemProperty] = this.selections[cf.field.id];
            }
          });
        }
      });

      // Reference-photo files are attached natively to the hat line in the
      // multipart add-to-cart request below (see _collectUploadFiles).

      // Calculate upgrade total
      const upgradeTotal = this.upgrades.length
        ? this.upgrades.reduce((sum, u) => sum + u.price, 0)
        : 0;

      // Add upgrade summary as visible line item properties
      if (upgradeTotal > 0) {
        properties['Customization Upgrades'] = this.formatMoney(upgradeTotal);
        properties['Upgrade Details'] = this.upgrades.map(u => `${u.name}: +${this.formatMoney(u.price)}`).join(', ');
      }

      // Add gallery source tracking for order attribution
      if (this._gallerySource) {
        properties['Gallery Source'] = this._gallerySource;
      }

      // Build edit URL so customer can return to builder with all selections pre-filled
      properties['_Builder URL'] = this._buildEditUrl();

      // Disable button during request
      const btn = this.els.addToCartBtn;
      btn.disabled = true;
      btn.classList.add('is-loading');
      const originalHtml = btn.innerHTML;
      btn.textContent = this._editingLineKey ? 'Updating cart...' : 'Adding to cart...';
      
      // If we're editing, remove the old item AND any associated fee items.
      // Fetch current cart, find fee line items for this product, remove all.
      if (this._editingLineKey) {
        try {
          // Fetch current cart to find associated fee items
          const cartResponse = await fetch('/cart.js', {
            headers: { 'Accept': 'application/json' }
          });
          const cart = await cartResponse.json();
          
          const updates = {};
          updates[this._editingLineKey] = 0; // Remove the hat itself
          
          // Find and remove any fee line items associated with this hat product
          const productIdStr = this.config.productId.toString();
          cart.items.forEach(item => {
            if (item.properties && item.properties._hat_customization_for === productIdStr) {
              updates[item.key] = 0;
              console.log('[HatBuilder] Found associated fee to remove:', item.key);
            }
          });
          
          console.log('[HatBuilder] Removing old items:', Object.keys(updates).join(', '));
          
          const removeResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates: updates })
          });
          if (!removeResponse.ok) {
            const errText = await removeResponse.text().catch(() => '');
            console.warn('[HatBuilder] cart/update non-OK:', removeResponse.status, errText);
            throw new Error('cart/update returned ' + removeResponse.status);
          }
          const result = await removeResponse.json().catch(() => ({}));
          console.log('[HatBuilder] Cart after remove — items:', result.item_count);
          this._editingLineKey = null;
        } catch (e) {
          console.error('[HatBuilder] Failed to remove old cart item:', e);
          this._showValidationError('Could not update your existing cart item. Please refresh the page and try again.');
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.innerHTML = originalHtml;
          return;
        }
      }

      try {
        // Reference-photo files, attached natively to the hat line item.
        const photoFileGroups = this._collectUploadFiles();
        const hasFiles = photoFileGroups.some(g => g.files && g.files.length);

        // Fee + free-can lines carry no files, so add them first via the JSON
        // items[] endpoint. When the hat has photos it must be added via a real
        // multipart form POST (Shopify's AJAX cart/add.js silently drops file
        // uploads); otherwise the AJAX path is used and keeps the cart drawer.
        const preItems = [];

        // Free hat can if this option set includes one
        if (this.selections.hatCanColor && this.freeCanVariants.length) {
          const colorVal = this.selections.hatCanColor.toLowerCase();
          const canVariant = this.freeCanVariants.find(v => v.title.toLowerCase() === colorVal);
          if (canVariant) {
            preItems.push({ id: canVariant.id, quantity: 1, properties: { '_free_with_hat': this.config.productId.toString() } });
            console.log('[HatBuilder] Adding free hat can variant:', canVariant.title);
          } else {
            console.warn('[HatBuilder] No hat can variant matched for color:', this.selections.hatCanColor);
          }
        }

        // Customization fee as a separate line item if upgrades exist
        const feeVariant = this._findFeeVariant(upgradeTotal);
        if (feeVariant) {
          preItems.push({
            id: feeVariant.id,
            quantity: 1,
            properties: {
              '_hat_customization_for': this.config.productId.toString()
            }
          });
          console.log('[HatBuilder] Adding fee variant:', feeVariant.title, this.formatMoney(feeVariant.price));
        } else if (upgradeTotal > 0) {
          console.warn('[HatBuilder] No fee variant found for upgrade total:', this.formatMoney(upgradeTotal));
        }

        // Step 1 — add fee/can items (if any). Track their keys so we can roll
        // them back if the hat add fails.
        let preAddedKeys = [];
        if (preItems.length) {
          const preResp = await fetch(this.config.routes.cartAdd, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: preItems })
          });
          if (!preResp.ok) {
            const errData = await preResp.json().catch(() => ({}));
            const reason = errData.description || errData.message || 'Unknown error';
            console.error('[HatBuilder] Cart API error (fee/can):', preResp.status, errData);
            throw new Error(reason);
          }
          const preResult = await preResp.json().catch(() => ({}));
          preAddedKeys = (preResult.items || []).map(i => i.key).filter(Boolean);
        }

        // Step 2 — add the hat.
        // When photos are attached, Shopify's AJAX cart/add.js can't carry the
        // files, so submit a real multipart form POST to /cart/add instead. This
        // uploads the photos natively and navigates to the cart page.
        if (hasFiles) {
          // Normalize non-JPEG/PNG (e.g. WebP) to JPEG so Shopify generates the
          // admin order thumbnail for the warehouse.
          for (const group of photoFileGroups) {
            group.files = await Promise.all(group.files.map(f => this._ensureThumbnailable(f)));
          }
          const addUrl = this.config.routes.cartAdd.replace('.js', '');
          const form = document.createElement('form');
          form.action = addUrl;
          form.method = 'post';
          form.enctype = 'multipart/form-data';
          form.setAttribute('hidden', '');
          const addHidden = (name, value) => {
            const inp = document.createElement('input');
            inp.type = 'hidden';
            inp.name = name;
            inp.value = value;
            form.appendChild(inp);
          };
          addHidden('id', this.config.variantId);
          addHidden('quantity', '1');
          Object.keys(properties).forEach(key => addHidden(`properties[${key}]`, properties[key]));
          photoFileGroups.forEach(group => {
            group.files.forEach((file, i) => {
              const propName = group.files.length === 1
                ? `properties[${group.propBase}]`
                : `properties[${group.propBase} ${i + 1}]`;
              const fileInput = document.createElement('input');
              fileInput.type = 'file';
              fileInput.name = propName;
              const dt = new DataTransfer();
              dt.items.add(file);
              fileInput.files = dt.files;
              form.appendChild(fileInput);
            });
          });
          document.body.appendChild(form);
          form.submit();
          return; // browser navigates to the cart page
        }

        // No files — the AJAX cart API is fine and keeps the cart drawer UX.
        const hatForm = new FormData();
        hatForm.append('id', this.config.variantId);
        hatForm.append('quantity', '1');
        Object.keys(properties).forEach(key => {
          hatForm.append(`properties[${key}]`, properties[key]);
        });
        photoFileGroups.forEach(group => {
          if (group.files.length === 1) {
            hatForm.append(`properties[${group.propBase}]`, group.files[0], group.files[0].name);
          } else {
            group.files.forEach((file, i) => {
              hatForm.append(`properties[${group.propBase} ${i + 1}]`, file, file.name);
            });
          }
        });

        // Files can be large/slow — allow more time than a plain add. Do NOT set
        // Content-Type; the browser adds the multipart boundary automatically.
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 60000);
        const response = await fetch(this.config.routes.cartAdd, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: hatForm,
          signal: controller.signal
        });
        clearTimeout(timeout);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const reason = errorData.description || errorData.message || 'Unknown error';
          console.error('[HatBuilder] Cart API error:', response.status, errorData);
          // Roll back the fee/can lines already added so the cart isn't left with
          // an orphaned upgrade fee.
          if (preAddedKeys.length) {
            const updates = {};
            preAddedKeys.forEach(k => { updates[k] = 0; });
            try {
              await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ updates: updates })
              });
            } catch (rollbackErr) {
              console.error('[HatBuilder] Rollback of fee/can failed:', rollbackErr);
            }
          }
          throw new Error(reason);
        }

        // Get the response data to find the line item key
        const result = await response.json();
        console.log('[HatBuilder] Cart add response:', result);
        
        // Find the hat line item in the response. A single-item form add may
        // return the item directly or wrapped in { items: [...] }.
        const hatItem = result.items
          ? (result.items.find(item => item.variant_id === this.config.variantId) || result.items[result.items.length - 1])
          : ((result.id || result.key) ? result : null);
        
        // If we got a line item key, update the _Builder URL property to include it for future edits
        if (hatItem && hatItem.key) {
          const editUrl = this._buildEditUrl(hatItem.key);
          console.log('[HatBuilder] Updating _Builder URL with line key:', hatItem.key);
          
          // Update the cart item with the proper edit URL
          try {
            await fetch(this.config.routes.cartChange, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({ 
                id: hatItem.key,
                quantity: hatItem.quantity,
                properties: {
                  ...hatItem.properties,
                  '_Builder URL': editUrl
                }
              })
            });
          } catch (e) {
            console.error('[HatBuilder] Failed to update edit URL:', e);
            // Non-critical, continue to cart
          }
        }

        // Success
        this.announce('Item added to cart successfully!');
        window.location.href = this.config.routes.cart;

      } catch (error) {
        console.error('Add to cart error:', error);
        const msg = error.message || 'Failed to add to cart. Please try again.';
        this.announce(msg);
        this._showValidationError(msg);
        btn.disabled = false;
        btn.classList.remove('is-loading');
        btn.innerHTML = originalHtml;
      }
    }

    // =========================================================================
    // Size Verification (Confirmation Step)
    // =========================================================================

    _resolveHatSizeLabel() {
      // 1. Active builder selection (most authoritative).
      if (this.selections.hatSize) return String(this.selections.hatSize);
      // 2. Fall back to looking up the current variant. Handles cases where the
      //    hatSize step wasn't injected for this product but a variant is set.
      const variants = Array.isArray(this.config.hatSizeVariants) ? this.config.hatSizeVariants : [];
      if (!variants.length) return null;
      const currentVariantId = parseInt(this.container.dataset.variantId, 10) || this.config.variantId;
      const v = variants.find(vv => Number(vv.id) === Number(currentVariantId));
      return v && v.option1 ? String(v.option1) : null;
    }

    _renderSizeVerification() {
      // Full-order review: read every selection from this.selections (never from
      // DOM). Rows mirror the "Your Selections" sidebar so desktop and mobile
      // show the same audit right above Add to Cart.
      // hatSize is rendered separately and pinned to the top — it must always
      // appear so the customer explicitly confirms the size, even if the
      // hatSize step wasn't injected for this product.
      // Hide shape-picker rows when the customer chose the photo path.
      const photoModeReview = this.selections.shapeMethod === 'photo';
      const reviewFields = [
        { stepId: 'shapeMethod', fieldId: null,         label: 'Shape Method' },
        { stepId: 'headShape',  fieldId: null,           label: 'Head Shape' },
        ...(photoModeReview ? [] : [
          { stepId: 'crownShape', fieldId: null,         label: 'Crown Shape' },
        ]),
        { stepId: 'brimStyle',  fieldId: 'brimSize',     label: 'Brim Size' },
        ...(photoModeReview ? [] : [
          { stepId: 'brimStyle',  fieldId: 'brimShape',  label: 'Brim Shape' },
          { stepId: 'brimStyle',  fieldId: 'brimFront',  label: 'Brim Front' },
        ]),
        { stepId: 'ribbon',     fieldId: 'ribbonType',   label: 'Ribbon' },
        { stepId: 'ribbon',     fieldId: 'boundEdge',    label: 'Edge' },
        { stepId: 'branding',   fieldId: 'wantBranding', label: 'Branding' }
      ];

      const rows = [];

      // Hat Size always at the top. Resolves from selections.hatSize (the
      // builder's source of truth) or falls back to looking up the current
      // variant — so this row appears even if step injection was skipped.
      const hatSizeLabel = this._resolveHatSizeLabel();
      const hatSizeStepIdx = this.steps.findIndex(s => s.id === 'hatSize');
      rows.push({
        stepIndex: hatSizeStepIdx,
        label: 'Hat Size',
        valueHtml: hatSizeLabel || '<span style="color:#c2410c">Not selected</span>',
        canChange: hatSizeStepIdx >= 0
      });

      reviewFields.forEach(entry => {
        const stepIndex = this.steps.findIndex(s => s.id === entry.stepId);
        if (stepIndex === -1) return;
        const step = this.steps[stepIndex];

        let field, value;
        if (entry.fieldId) {
          field = step.fields?.find(f => f.id === entry.fieldId);
          if (!field) return;
          value = this.selections[field.id];
        } else {
          field = step;
          value = this.selections[step.id];
        }

        const hasValue = value !== undefined && value !== null && value !== '';
        if (!hasValue) {
          if (!step.required) return;
          rows.push({ stepIndex, label: entry.label, valueHtml: '<span style="color:#c2410c">Not selected</span>' });
          return;
        }
        if (value === 'none' || value === 'no') {
          rows.push({ stepIndex, label: entry.label, valueHtml: 'None' });
          return;
        }

        const displayText = this._getFieldSelectionLabel(field, value);
        rows.push({ stepIndex, label: entry.label, valueHtml: displayText, canChange: true });
      });

      // Reference photos — render local thumbnails inline so the customer can
      // verify the visual references before submitting. Files upload to Shopify
      // natively at add-to-cart, so we preview from the in-memory File objects.
      const photoEntries = (this._uploadedFiles && this._uploadedFiles.photos) || [];
      if (photoEntries.length) {
        const photosStepIdx = this.steps.findIndex(s => s.id === 'notesAndPhotos');
        const thumbsHtml = photoEntries.map(f => (
          `<span style="display:inline-block"><img src="${f.localUrl}" alt="${this._escapeHtml(f.name)}" style="max-width:48px;max-height:48px;border-radius:4px;border:1px solid #ddd;object-fit:cover;" /></span>`
        )).join(' ');
        rows.push({
          stepIndex: photosStepIdx,
          label: 'Reference Photos',
          valueHtml: `<div style="display:flex;gap:4px;flex-wrap:wrap;align-items:center;">${thumbsHtml}</div>`,
          canChange: photosStepIdx >= 0
        });
      }

      if (!rows.length) return '';

      const rowsHtml = rows.map(r => `
        <div class="hb-size-verify__item">
          <span class="hb-size-verify__label">${r.label}</span>
          <span class="hb-size-verify__value">${r.valueHtml}</span>
          ${r.canChange !== false ? `<button type="button" class="hb-size-verify__edit" data-goto-step="${r.stepIndex}" aria-label="Change ${r.label}">Change</button>` : ''}
        </div>
      `).join('');

      return `
        <div class="hb-size-verify" role="alert">
          <div class="hb-size-verify__header">
            <svg class="hb-size-verify__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <h4 class="hb-size-verify__title">Please Review Your Hat</h4>
          </div>
          <p class="hb-size-verify__note">This is your last chance to make changes. Double-check every selection — once shaped, the hat cannot be returned.</p>
          <div class="hb-size-verify__items">
            ${rowsHtml}
          </div>
        </div>
      `;
    }

    // =========================================================================
    // Utilities
    // =========================================================================

    _getFieldSelectionLabel(field, value) {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (value === 'Yes' || value === 'No') return value;

      // Check direct options or library ref
      const options = field.options || this.getLibraryOptions(field.optionsRef) || [];
      const opt = options.find(o => o.value === value);
      if (opt) return opt.label;

      // Check category-based options (crown step has categories instead of direct options)
      if (field.categories) {
        for (const cat of field.categories) {
          const catOptions = this.getLibraryOptions(cat.optionsRef) || [];
          const catOpt = catOptions.find(o => o.value === value);
          if (catOpt) return catOpt.label;
        }
      }

      return value;
    }

    _getSelectionPriceStatus(field, value) {
      if (!value || value === '' || value === 'none' || value === 'no') {
        return { type: 'none', price: 0 };
      }

      // Check direct options or library ref
      const options = field.options || this.getLibraryOptions(field.optionsRef) || [];
      const selectedOpt = options.find(o => o.value === value);
      if (selectedOpt && selectedOpt.price && selectedOpt.price > 0) {
        return { type: 'upgrade', price: selectedOpt.price * 100 };
      }

      // Check category-based options (crown step)
      if (field.categories) {
        for (const cat of field.categories) {
          const catOptions = this.getLibraryOptions(cat.optionsRef) || [];
          const catOpt = catOptions.find(o => o.value === value);
          if (catOpt && catOpt.price && catOpt.price > 0) {
            return { type: 'upgrade', price: catOpt.price * 100 };
          }
        }
      }

      // Check tiered pricing (branding text)
      if (field.pricing && field.pricing.type === 'tiered' && value) {
        const charCount = value.length;
        if (charCount > 0) {
          let price = 0;
          for (const tier of field.pricing.tiers) {
            if (charCount < tier.from) continue;
            if (tier.price) price += tier.price;
            if (tier.pricePerUnit) {
              const charsInTier = Math.max(0, Math.min(charCount, tier.to) - tier.from + 1);
              if (charsInTier > 0) price += charsInTier * tier.pricePerUnit;
            }
          }
          if (price > 0) return { type: 'upgrade', price: price * 100 };
        }
      }

      return { type: 'included', price: 0 };
    }

    getLibraryOptions(ref) {
      if (!ref || !this.config.libraries) return null;

      // Parse ref like "sharedLibraries.crowns.traditional"
      const parts = ref.replace('sharedLibraries.', '').split('.');
      let data = this.config.libraries;

      for (const part of parts) {
        if (!data || data[part] === undefined) return null;
        data = data[part];
      }

      return Array.isArray(data) ? data : null;
    }

    getImageUrl(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      
      // Check for user-editable image config (loaded from crown-images-config.js)
      if (window.CROWN_IMAGE_CONFIG) {
        // Extract image type and ID from path
        let imageId, configSection;
        
        if (path.startsWith('crowns/')) {
          imageId = path.replace('crowns/', '').replace(/\.(webp|jpg|png|svg)$/, '');
          configSection = window.CROWN_IMAGE_CONFIG.crowns;
        } else if (path.startsWith('brims/front-')) {
          imageId = path.replace('brims/front-', '').replace(/\.(webp|jpg|png|svg)$/, '');
          configSection = window.CROWN_IMAGE_CONFIG.brimFronts;
        } else if (path.startsWith('brims/shape-')) {
          imageId = path.replace('brims/shape-', '').replace(/\.(webp|jpg|png|svg)$/, '');
          configSection = window.CROWN_IMAGE_CONFIG.brimShapes;
        }
        
        if (configSection && configSection[imageId]) {
          const override = configSection[imageId];
          return override.startsWith('http') ? override : this.config.filesCdnBase + override.replace(/\//g, '_');
        }
      }
      
      // Shopify Files is flat (no folders) - convert slashes to underscores
      // e.g. "crowns/association.webp" -> "crowns_association.webp"
      const flatName = path.replace(/\//g, '_');
      // Use CDN base from Liquid config (e.g. "//cdn.shopify.com/s/files/1/0478/.../files/")
      const base = this.config.filesCdnBase;
      if (base) return base + flatName;
      // Fallback to store domain path
      return '//' + window.location.host + '/cdn/shop/files/' + flatName;
    }

    /**
     * Builds an onerror handler: tries .png then .svg, then shows text placeholder.
     * Max 3 requests per missing image (original + .png + .svg).
     */
    _buildImageFallback() {
      if (this._imageFallbackStr) return this._imageFallbackStr;
      this._imageFallbackStr = `var f=this.dataset.fb|0;var b=this.src.replace(/\\.[^.]+$/,'');if(f==0){this.dataset.fb=1;this.src=b+'.png';return}if(f==1){this.dataset.fb=2;this.src=b+'.svg';return}this.style.display='none';if(this.nextElementSibling)this.nextElementSibling.style.display='flex';`;
      return this._imageFallbackStr;
    }

    isLightColor(color) {
      if (!color) return false;
      const hex = color.replace('#', '');
      if (hex.length !== 6) return false;
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 180;
    }

    checkCondition(condition) {
      for (const [key, values] of Object.entries(condition)) {
        const selected = this.selections[key];
        if (!selected || !values.includes(selected)) {
          return false;
        }
      }
      return true;
    }

    formatMoney(cents) {
      const format = this.config.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
    }

    // =========================================================================
    // File Upload Helpers
    // =========================================================================

    // Native Shopify upload: hold the File in memory and show a local preview.
    // The actual upload happens as a line-item-property file when the customer
    // adds the hat to the cart (see the add-to-cart handler).
    _addLocalFile(file, fieldId, previewsContainer, statusEl) {
      const localUrl = URL.createObjectURL(file);
      if (!this._uploadedFiles[fieldId]) this._uploadedFiles[fieldId] = [];
      this._uploadedFiles[fieldId].push({
        file: file,
        localUrl: localUrl,
        name: file.name,
        size: file.size
      });

      // Reference photos aren't a text property — store filenames only so the
      // builder's "has a value" checks, required-validation, and summary work.
      this.selections[fieldId] = this._uploadedFiles[fieldId].map(f => f.name).join(', ');

      this._renderUploadPreviews(fieldId, previewsContainer);
      previewsContainer.hidden = false;

      this._showUploadStatus(statusEl, `${file.name} added`, 'success');
      this.announce(`${file.name} added`);
      setTimeout(() => {
        if (statusEl) statusEl.hidden = true;
      }, 3000);
    }

    // Gather uploaded files across all fileUpload fields, paired with the
    // line-item property they should be attached under at add-to-cart.
    _collectUploadFiles() {
      const groups = [];
      if (!this._uploadedFiles) return groups;
      this.steps.forEach(s => {
        const fields = (s.type === 'multiField' && s.fields) ? s.fields : [s];
        fields.forEach(field => {
          if (field.type === 'fileUpload' && field.lineItemProperty) {
            const entries = this._uploadedFiles[field.id] || [];
            const files = entries.map(e => e.file).filter(Boolean);
            if (files.length) groups.push({ propBase: field.lineItemProperty, files: files });
          }
        });
      });
      return groups;
    }

    // Shopify's admin only auto-generates order thumbnails for JPEG/PNG line-item
    // uploads — WebP (and other formats) show as a bare link. Convert anything
    // that isn't already JPEG/PNG to JPEG so the warehouse always sees a preview.
    // Falls back to the original file if conversion isn't possible.
    async _ensureThumbnailable(file) {
      if (!file || file.type === 'image/jpeg' || file.type === 'image/png') return file;
      try {
        const bitmap = await createImageBitmap(file);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        if (bitmap.close) bitmap.close();
        const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
        if (!blob) return file;
        const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg';
        return new File([blob], newName, { type: 'image/jpeg' });
      } catch (e) {
        console.warn('[HatBuilder] Could not convert image to JPEG, using original:', e);
        return file;
      }
    }

    _renderUploadPreviews(fieldId, container) {
      const files = this._uploadedFiles[fieldId] || [];
      container.innerHTML = files.map((f, i) => {
        const imgSrc = f.localUrl || '';

        return `
          <div class="hb-upload__preview" data-file-index="${i}">
            <img src="${imgSrc}"
                 alt="${this._escapeHtml(f.name)}"
                 class="hb-upload__preview-img">
            <div class="hb-upload__preview-info">
              <span class="hb-upload__preview-name">${this._escapeHtml(f.name)}</span>
            </div>
            <button type="button"
                    class="hb-upload__preview-remove"
                    aria-label="Remove ${this._escapeHtml(f.name)}"
                    data-file-index="${i}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        `;
      }).join('');
    }

    _showUploadStatus(statusEl, message, type) {
      if (!statusEl) return;
      statusEl.hidden = false;
      statusEl.textContent = message;
      statusEl.className = 'hb-upload__status';
      if (type === 'error') statusEl.classList.add('hb-upload__status--error');
      if (type === 'success') statusEl.classList.add('hb-upload__status--success');
    }

    _escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    announce(message) {
      if (this.els.announcer) {
        this.els.announcer.textContent = message;
        setTimeout(() => {
          this.els.announcer.textContent = '';
        }, 5000);
      }
    }
  }

  // Initialize all hat builder instances on the page
  const builders = document.querySelectorAll('.cowlot-hat-builder');
  builders.forEach(initHatBuilder);

})();
